/**
 * Send Package Page - Enhanced with GPS Location & Address Autofill
 * Flow: Pickup (GPS/Address) → Dropoff (Address autofill) → Size → Quote → Compliance → Payment → Success
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import api from "../lib/api";
import { 
  ArrowLeft, Package, MapPin, User, Phone, Navigation,
  Loader2, CheckCircle, CreditCard, Receipt, Truck,
  Clock, DollarSign, AlertCircle, Building, Mail, Link2, Copy, MessageCircle,
  ShieldCheck, AlertTriangle, XCircle, FileText, Locate, Search, Camera, ImagePlus, Star,
  Home, Building2, Shield, Printer, KeyRound
} from "lucide-react";
import { PudoSelector } from "../components/PudoSelector";
import LaunchPromoBanner from "../components/LaunchPromoBanner";

const PACKAGE_SIZES = [
  { value: "small", label: "Small", desc: "Up to 5 lbs, fits in hand", price: 800 },
  { value: "medium", label: "Medium", desc: "5-10 lbs, shoebox size", price: 1200 },
  { value: "large", label: "Large", desc: "10-20 lbs, backpack size", price: 1800 },
  { value: "xlarge", label: "Extra Large", desc: "20-50 lbs, suitcase size", price: 2500 }
];

const CONTENT_CATEGORIES = [
  { value: "DOCUMENTS", label: "Documents" },
  { value: "CLOTHING", label: "Clothing" },
  { value: "ELECTRONICS", label: "Electronics" },
  { value: "FOOD_NONPERISHABLE", label: "Food (non-perishable)" },
  { value: "HOUSEHOLD", label: "Household items" },
  { value: "OTHER_NONPROHIBITED", label: "Other (non-prohibited)" }
];

const parishes = [
  "Kingston", "St. Andrew", "St. Thomas", "Portland", "St. Mary",
  "St. Ann", "Trelawny", "St. James", "Hanover", "Westmoreland",
  "St. Elizabeth", "Manchester", "Clarendon", "St. Catherine"
];

// Popular Jamaica landmarks with GPS coordinates for quick selection
const JAMAICA_LANDMARKS = {
  "JUTC Halfway Tree": { lat: 18.0157, lng: -76.7898, parish: "St. Andrew" },
  "JUTC Half Way Tree": { lat: 18.0157, lng: -76.7898, parish: "St. Andrew" },
  "Constant Spring": { lat: 18.0258, lng: -76.7807, parish: "St. Andrew" },
  "Half Way Tree": { lat: 18.0157, lng: -76.7898, parish: "St. Andrew" },
  "Halfway Tree": { lat: 18.0157, lng: -76.7898, parish: "St. Andrew" },
  "Downtown Kingston": { lat: 17.9714, lng: -76.7920, parish: "Kingston" },
  "King Street Kingston": { lat: 17.9714, lng: -76.7920, parish: "Kingston" },
  "New Kingston": { lat: 18.0075, lng: -76.7833, parish: "St. Andrew" },
  "Liguanea": { lat: 18.0192, lng: -76.7633, parish: "St. Andrew" },
  "Cross Roads": { lat: 18.0042, lng: -76.7875, parish: "St. Andrew" },
  "Portmore": { lat: 17.9500, lng: -76.8833, parish: "St. Catherine" },
  "Spanish Town": { lat: 17.9917, lng: -76.9550, parish: "St. Catherine" },
  "Montego Bay": { lat: 18.4762, lng: -77.8939, parish: "St. James" },
  "Ocho Rios": { lat: 18.4074, lng: -77.1051, parish: "St. Ann" },
  "Mandeville": { lat: 18.0419, lng: -77.5033, parish: "Manchester" },
  "May Pen": { lat: 17.9619, lng: -77.2478, parish: "Clarendon" },
  "Negril": { lat: 18.2678, lng: -78.3492, parish: "Westmoreland" },
  "Port Antonio": { lat: 18.1747, lng: -76.4500, parish: "Portland" },
  "Norman Manley Airport": { lat: 17.9356, lng: -76.7875, parish: "Kingston" },
  "Sangster Airport": { lat: 18.5033, lng: -77.9133, parish: "St. James" }
};


// Resolve parish from GPS coordinates using bounding boxes
const resolveParishFromCoords = (lat, lng) => {
  const PARISH_BOUNDS = {
    "Kingston": { latMin: 17.95, latMax: 18.02, lngMin: -76.82, lngMax: -76.75 },
    "St. Andrew": { latMin: 17.95, latMax: 18.15, lngMin: -76.85, lngMax: -76.70 },
    "St. Thomas": { latMin: 17.85, latMax: 18.10, lngMin: -76.55, lngMax: -76.20 },
    "Portland": { latMin: 18.05, latMax: 18.30, lngMin: -76.60, lngMax: -76.20 },
    "St. Mary": { latMin: 18.15, latMax: 18.40, lngMin: -77.00, lngMax: -76.55 },
    "St. Ann": { latMin: 18.20, latMax: 18.50, lngMin: -77.40, lngMax: -77.00 },
    "Trelawny": { latMin: 18.20, latMax: 18.50, lngMin: -77.70, lngMax: -77.40 },
    "St. James": { latMin: 18.25, latMax: 18.55, lngMin: -78.00, lngMax: -77.65 },
    "Hanover": { latMin: 18.30, latMax: 18.50, lngMin: -78.25, lngMax: -77.95 },
    "Westmoreland": { latMin: 18.10, latMax: 18.40, lngMin: -78.40, lngMax: -78.00 },
    "St. Elizabeth": { latMin: 17.90, latMax: 18.20, lngMin: -77.90, lngMax: -77.50 },
    "Manchester": { latMin: 17.90, latMax: 18.15, lngMin: -77.60, lngMax: -77.30 },
    "Clarendon": { latMin: 17.80, latMax: 18.10, lngMin: -77.40, lngMax: -77.00 },
    "St. Catherine": { latMin: 17.85, latMax: 18.15, lngMin: -77.10, lngMax: -76.75 },
  };
  for (const [parish, b] of Object.entries(PARISH_BOUNDS)) {
    if (lat >= b.latMin && lat <= b.latMax && lng >= b.lngMin && lng <= b.lngMax) return parish;
  }
  return null;
};

const SendPackage = ({ operatorMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setSession, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  // R29 — guest checkout gate
  const [showGuestGate, setShowGuestGate] = useState(false);
  const [gateTab, setGateTab] = useState('guest'); // 'signin' | 'guest'
  const [gateBusy, setGateBusy] = useState(false);
  const [gateStep, setGateStep] = useState('form'); // 'form' | 'code'
  const [gsEmail, setGsEmail] = useState('');
  const [gsPhone, setGsPhone] = useState('');
  const [gsName, setGsName] = useState('');
  const [gsCode, setGsCode] = useState('');
  const [signinEmail, setSigninEmail] = useState('');
  const [signinPassword, setSigninPassword] = useState('');
  const [lcMode, setLcMode] = useState(false);
  const [lcSent, setLcSent] = useState(false);
  const [lcCode, setLcCode] = useState('');
  const [lcBusy, setLcBusy] = useState(false);
  const [pendingAuthCheckout, setPendingAuthCheckout] = useState(false);  // R29 blocker-1 fix
  const [confirmedPackage, setConfirmedPackage] = useState(null);
  // Operator mode starts at step 0 (customer info), regular mode starts at step 1
  const [step, setStep] = useState(operatorMode ? 0 : 1);
  const [fieldErrors, setFieldErrors] = useState({});
  
  // ========== OPERATOR MODE STATE ==========
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [operatorNotes, setOperatorNotes] = useState("");
  const [assistedBookingResult, setAssistedBookingResult] = useState(null);
  const [paymentLinkOptions, setPaymentLinkOptions] = useState(null);

  // Inline error helper – renders a red message under a field
  const FieldError = ({ name }) => fieldErrors[name] ? (
    <p className="flex items-center gap-1 text-red-400 text-xs mt-1 animate-pulse">
      <span>&#9888;</span> {fieldErrors[name]}
    </p>
  ) : null;

  // Clear a single error when user starts fixing it
  const clearErr = (name) => {
    if (fieldErrors[name]) setFieldErrors(prev => { const n = {...prev}; delete n[name]; return n; });
  };
  const [isLoading, setIsLoading] = useState(false);
  const [createdPackage, setCreatedPackage] = useState(null);
  const [showLabel, setShowLabel] = useState(false);
  const [showSetPwd, setShowSetPwd] = useState(false);
  const [newPwd, setNewPwd] = useState('');
  const [newPwd2, setNewPwd2] = useState('');
  const [pwdBusy, setPwdBusy] = useState(false);
  
  // Urgent Delivery
  const [isUrgent, setIsUrgent] = useState(false);
  const [isFragile, setIsFragile] = useState(false);
  const [urgentOfferAmount, setUrgentOfferAmount] = useState("");
  const URGENT_MIN = 800;
  const URGENT_HOT = 1500;
  
  // GPS & Location
  const [useGPS, setUseGPS] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [searchingAddress, setSearchingAddress] = useState(false);
  const searchTimerRef = useRef(null);
  const quoteTimerRef = useRef(null);
  
  // Recipient
  const [recipientName, setRecipientName] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  
  // Pickup
  const [pickupStreet, setPickupStreet] = useState("");
  const [pickupCity, setPickupCity] = useState("");
  const [pickupParish, setPickupParish] = useState("Kingston");
  const [pickupLandmark, setPickupLandmark] = useState("");
  const [pickupConfirmed, setPickupConfirmed] = useState(false);
  
  // Dropoff
  const [dropoffStreet, setDropoffStreet] = useState("");
  const [dropoffCity, setDropoffCity] = useState("");
  const [dropoffParish, setDropoffParish] = useState("Kingston");
  const [dropoffLandmark, setDropoffLandmark] = useState("");
  const [dropoffConfirmed, setDropoffConfirmed] = useState(false);

  // Rebook prefill from navigation state
  useEffect(() => {
    const rebook = location.state?.rebook;
    if (!rebook) return;

    // Prefill recipient
    if (rebook.recipient_name) setRecipientName(rebook.recipient_name);
    if (rebook.recipient_phone) setRecipientPhone(rebook.recipient_phone);
    if (rebook.recipient_email) setRecipientEmail(rebook.recipient_email);

    // Prefill pickup
    const pickup = rebook.pickup_address || {};
    if (pickup.street) setPickupStreet(pickup.street);
    if (pickup.city) setPickupCity(pickup.city);
    if (pickup.parish) setPickupParish(pickup.parish);
    if (pickup.lat && pickup.lng) setPickupCoords({ lat: pickup.lat, lng: pickup.lng });

    // Prefill dropoff
    const dropoff = rebook.dropoff_address || {};
    if (dropoff.street) setDropoffStreet(dropoff.street);
    if (dropoff.city) setDropoffCity(dropoff.city);
    if (dropoff.parish) setDropoffParish(dropoff.parish);
    if (dropoff.lat && dropoff.lng) setDropoffCoords({ lat: dropoff.lat, lng: dropoff.lng });

    // Prefill package details
    if (rebook.package_size) setPackageSize(rebook.package_size);
    if (rebook.estimated_weight_lbs) setPackageWeight(String(rebook.estimated_weight_lbs));
    if (rebook.package_contents) setPackageContents(rebook.package_contents);
    if (rebook.is_fragile) setIsFragile(rebook.is_fragile);

    toast.success("Rebook: Details prefilled from your previous shipment");
    // Clear location state to prevent re-prefill on refresh
    window.history.replaceState({}, document.title);
  }, [location.state]);

  // Location Sharing
  const [locationShareToken, setLocationShareToken] = useState(null);
  const [locationShareLoading, setLocationShareLoading] = useState(false);
  const [receiverLocation, setReceiverLocation] = useState(null);
  const [pollingLocation, setPollingLocation] = useState(false);
  
  // Package
  const [packageSize, setPackageSize] = useState("small");
  const [packageWeight, setPackageWeight] = useState("");
  const [packageContents, setPackageContents] = useState("");

  // Package Photos (3 required: top, side, reference)
  const [packagePhotos, setPackagePhotos] = useState({ top: null, side: null, reference: null });
  const [photoUploading, setPhotoUploading] = useState(false);

  // Multi-Package Support - Array of packages for same address delivery
  const [packages, setPackages] = useState([]);
  const [isMultiPackage, setIsMultiPackage] = useState(false);

  // Saved Locations
  const [savedPickupLocations, setSavedPickupLocations] = useState([]);
  const [savedDeliveryLocations, setSavedDeliveryLocations] = useState([]);
  const [showSaveLocationModal, setShowSaveLocationModal] = useState(false);
  const [lastBookingId, setLastBookingId] = useState(null);
  const [wantSavePickup, setWantSavePickup] = useState(false);
  const [wantSaveDelivery, setWantSaveDelivery] = useState(false);


  const [contentsCategory, setContentsCategory] = useState("OTHER_NONPROHIBITED");
  
  // Quote & Payment
  const [quote, setQuote] = useState(null);
  const [discountCode, setDiscountCode] = useState(searchParams.get('ref') || "");
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountError, setDiscountError] = useState("");
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const applyDiscount = async () => {
    const code = (discountCode || "").trim();
    if (!code) { setDiscountError("Enter a code"); return; }
    setApplyingDiscount(true); setDiscountError("");
    try {
      const amount = (quote?.total_price || 0);
      const res = await api.post("/referrals/preview", { code, amount });
      setAppliedDiscount(res.data);
      toast.success(`${res.data.label || "Discount"} applied — ${res.data.discount_percent}% off!`);
    } catch (e) {
      setAppliedDiscount(null);
      setDiscountError(e.response?.data?.detail || "Invalid or ineligible code");
    } finally { setApplyingDiscount(false); }
  };
  const [distance, setDistance] = useState(null);
  
  // Compliance - detailed prohibited items checks
  const [complianceAcknowledged, setComplianceAcknowledged] = useState(false);
  const [containsCash, setContainsCash] = useState(false);
  const [containsWeapons, setContainsWeapons] = useState(false);
  const [containsDrugs, setContainsDrugs] = useState(false);
  const [containsHazmat, setContainsHazmat] = useState(false);
  const [complianceBlocked, setComplianceBlocked] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  
  // PUDO
  const [pudos, setPudos] = useState([]);
  const [loadingPudos, setLoadingPudos] = useState(false);
  const [dropoffMode, setDropoffMode] = useState("ADDRESS"); // ADDRESS or PUDO
  const [dropoffPudoId, setDropoffPudoId] = useState("");
  const [pickupMode, setPickupMode] = useState("ADDRESS"); // ADDRESS or PUDO
  const [pickupPudoId, setPickupPudoId] = useState("");

  // Check for prohibited items
  useEffect(() => {
    if (containsCash || containsWeapons || containsDrugs || containsHazmat) {
      setComplianceBlocked(true);
      if (containsCash) setBlockReason("Cash and monetary instruments cannot be shipped");
      else if (containsWeapons) setBlockReason("Weapons, ammunition and explosives are prohibited");
      else if (containsDrugs) setBlockReason("Illegal drugs and controlled substances are prohibited");
      else if (containsHazmat) setBlockReason("Hazardous materials require special handling");
    } else {
      setComplianceBlocked(false);
      setBlockReason("");
    }
  }, [containsCash, containsWeapons, containsDrugs, containsHazmat]);

  // Set sender email from logged-in user
  useEffect(() => {
    if (user?.email && !senderEmail) {
      setSenderEmail(user.email);
    }
  }, [user]);

  
  // Handle photo upload
  const handlePhotoUpload = async (type, file) => {
    if (!file) return;
    setPhotoUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", "package_photo");
    try {
      const response = await api.post("/uploads/image", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      console.log("Upload response:", response.data);
      if (response.data && response.data.url) {
        setPackagePhotos(prev => {
          const updated = { ...prev, [type]: response.data.url };
          console.log("Updated photos:", updated);
          return updated;
        });
        toast.success(type.charAt(0).toUpperCase() + type.slice(1) + " photo uploaded");
      } else {
        console.error("No URL in response:", response.data);
        toast.error("Upload failed - no URL returned");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload photo: " + (error.response?.data?.detail || error.message));
    } finally {
      setPhotoUploading(false);
    }
  };

  const allPhotosUploaded = packagePhotos.top && packagePhotos.side && packagePhotos.reference;
  const hasMinimumPhotos = packagePhotos.top || packagePhotos.side || packagePhotos.reference; // At least 1 photo required
  const photoCount = [packagePhotos.top, packagePhotos.side, packagePhotos.reference].filter(Boolean).length;

  // Multi-Package Functions
  const addPackageToList = () => {
    if (!packageSize || !packageWeight || parseFloat(packageWeight) <= 0) {
      toast.error("Please fill in package size and weight first");
      return;
    }
    // Photos are optional
    if (!contentsCategory) {
      toast.error("Please select a contents category");
      return;
    }
    
    const newPackage = {
      id: Date.now(),
      size: packageSize,
      weight: parseFloat(packageWeight),
      contents: packageContents,
      category: contentsCategory,
      photos: [packagePhotos.top, packagePhotos.side, packagePhotos.reference].filter(Boolean),
      isFragile: isFragile,
      sizeLabel: PACKAGE_SIZES.find(s => s.value === packageSize)?.label || packageSize,
      basePrice: PACKAGE_SIZES.find(s => s.value === packageSize)?.price || 800
    };
    
    setPackages(prev => [...prev, newPackage]);
    setIsMultiPackage(true);
    
    // Reset form for next package
    setPackageSize("small");
    setPackageWeight("");
    setPackageContents("");
    setPackagePhotos({ top: null, side: null, reference: null });
    setContentsCategory("OTHER_NONPROHIBITED");
    setIsFragile(false);
    
    toast.success(`Package #${packages.length + 1} added! Add more or proceed to quote.`);
  };
  
  const removePackage = (packageId) => {
    setPackages(prev => prev.filter(p => p.id !== packageId));
    if (packages.length <= 1) {
      setIsMultiPackage(false);
    }
    toast.success("Package removed");
  };
  
  // Calculate total weight across all packages
  const getTotalWeight = () => {
    const currentWeight = parseFloat(packageWeight) || 0;
    const packagesWeight = packages.reduce((sum, pkg) => sum + pkg.weight, 0);
    return packagesWeight + (isMultiPackage ? 0 : currentWeight);
  };
  
  // Calculate total base price across all packages  
  const getTotalBasePrice = () => {
    const currentBasePrice = PACKAGE_SIZES.find(s => s.value === packageSize)?.price || 800;
    const packagesBasePrice = packages.reduce((sum, pkg) => sum + pkg.basePrice, 0);
    return packagesBasePrice + (isMultiPackage && packages.length > 0 ? 0 : currentBasePrice);
  };

  
  // Fetch saved locations on mount
  useEffect(() => {
    const fetchSavedLocations = async () => {
      if (!user) return;
      try {
        const response = await api.get("/locations/saved");
        if (response.data.locations) {
          setSavedPickupLocations(response.data.locations.filter(l => l.type === "pickup"));
          setSavedDeliveryLocations(response.data.locations.filter(l => l.type === "delivery"));
        }
      } catch (error) {
        console.log("No saved locations");
      }
    };
    fetchSavedLocations();
  }, [user]);

  // Apply saved pickup location
  const applySavedPickup = (loc) => {
    if (loc.address) {
      setPickupStreet(loc.address.street || "");
      setPickupCity(loc.address.city || "");
      setPickupParish(loc.address.parish || "");
      if (loc.address.coordinates) {
        setPickupLat(loc.address.coordinates.lat);
        setPickupLng(loc.address.coordinates.lng);
      }
      toast.success("Pickup location applied");
    }
  };

  // Apply saved delivery location
  const applySavedDelivery = (loc) => {
    if (loc.address) {
      setDropoffStreet(loc.address.street || "");
      setDropoffCity(loc.address.city || "");
      setDropoffParish(loc.address.parish || "");
      if (loc.address.coordinates) {
        setDropoffLat(loc.address.coordinates.lat);
        setDropoffLng(loc.address.coordinates.lng);
      }
    }
    if (loc.recipient_name) setRecipientName(loc.recipient_name);
    if (loc.recipient_email) setRecipientEmail(loc.recipient_email);
    if (loc.recipient_phone) setRecipientPhone(loc.recipient_phone);
    toast.success("Delivery location applied");
  };

  // Save locations after successful booking
  const handleSaveLocations = async (savePickup, saveDelivery) => {
    if (!lastBookingId) return;
    try {
      await api.post("/locations/save-from-booking", null, {
        params: {
          package_id: lastBookingId,
          save_pickup: savePickup,
          save_delivery: saveDelivery
        }
      });
      toast.success("Locations saved for faster checkout!");
      setShowSaveLocationModal(false);
    } catch (error) {
      toast.error("Failed to save locations");
    }
  };


  // Fetch approved PUDOs
  const fetchPudos = useCallback(async () => {
    try {
      setLoadingPudos(true);
      const res = await api.get("/pudo/list?active_only=true&approved_only=true");
      setPudos(res.data?.pudos || []);
    } catch (error) {
      console.error("Failed to fetch PUDOs:", error);
    } finally {
      setLoadingPudos(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchPudos();
    }
  }, [user, fetchPudos]);

  // Handle PUDO selection for dropoff - auto-fill GPS coordinates
  const handlePudoSelect = (pudoId) => {
    setDropoffPudoId(pudoId);
    const selectedPudo = pudos.find(p => p.id === pudoId);
    if (selectedPudo) {
      // Auto-fill GPS coordinates from PUDO location
      setDropoffCoords({ lat: selectedPudo.lat, lng: selectedPudo.lng });
      setDropoffStreet(selectedPudo.address?.street || '');
      setDropoffCity(selectedPudo.address?.city || '');
      setDropoffParish(selectedPudo.address?.parish || 'Kingston');
      toast.success(`PUDO "${selectedPudo.name}" selected - GPS coordinates auto-filled`);
    }
  };

  // Handle PUDO selection for pickup - sender drops off at PUDO
  const handlePickupPudoSelect = (pudoId) => {
    setPickupPudoId(pudoId);
    const selectedPudo = pudos.find(p => p.id === pudoId);
    if (selectedPudo) {
      // Auto-fill GPS coordinates from PUDO location
      setPickupCoords({ lat: selectedPudo.lat, lng: selectedPudo.lng });
      setPickupStreet(selectedPudo.address?.street || '');
      setPickupCity(selectedPudo.address?.city || '');
      setPickupParish(selectedPudo.address?.parish || 'Kingston');
      setPickupConfirmed(true);
      toast.success(`Drop-off at "${selectedPudo.name}" selected - You will bring the package here`);
    }
  };

  // Handle Stripe payment success callback
  useEffect(() => {
    const success = searchParams.get("success");
    const packageId = searchParams.get("package_id");
    
    if (success === "true" && packageId && !paymentSuccess) {
      // Confirm the payment and create package
      const confirmPayment = async () => {
        try {
          setIsLoading(true);
          const response = await api.post("/packages/confirm-by-package-id", {
            package_id: searchParams.get("package_id")
          });
          
          if (response.data.success) {
            setPaymentSuccess(true);
            setConfirmedPackage(response.data);
            setStep(6); // Move to success step
            toast.success("Payment confirmed! Package created successfully.");
            
            // Save locations if user requested
            const pkgId = searchParams.get("package_id");
            if ((wantSavePickup || wantSaveDelivery) && pkgId) {
              try {
                await api.post("/locations/save-from-booking", null, {
                  params: {
                    package_id: pkgId,
                    save_pickup: wantSavePickup,
                    save_delivery: wantSaveDelivery
                  }
                });
                toast.success("Locations saved for faster checkout next time!");
              } catch (saveError) {
                console.log("Failed to save locations:", saveError);
              }
            }
            
            // Clear URL params
            setSearchParams({});
          }
        } catch (error) {
          console.error("Failed to confirm payment:", error);
          toast.error("Payment confirmation failed. Please contact support.");
        } finally {
          setIsLoading(false);
        }
      };
      
      confirmPayment();
    }
  }, [searchParams, paymentSuccess, setSearchParams]);

  // Get current GPS location
  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }
    
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setPickupCoords(coords);
        
        // Auto-fill parish from GPS coordinates
        const detectedParish = resolveParishFromCoords(coords.lat, coords.lng);
        if (detectedParish) {
          setPickupParish(detectedParish);
          toast.success("Location found - " + detectedParish + " parish detected");
        } else {
          toast.success("GPS location captured");
        }

        setGettingLocation(false);
      },
      (error) => {
        setGettingLocation(false);
        toast.error("Unable to get location: " + error.message);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  // Search address suggestions - includes local landmarks first
  const searchAddress = useCallback(async (query, isDropoff = false) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    
    setSearchingAddress(true);
    
    // First check local landmarks
    const queryLower = query.toLowerCase();
    const matchedLandmarks = Object.entries(JAMAICA_LANDMARKS)
      .filter(([name]) => name.toLowerCase().includes(queryLower) || queryLower.includes(name.toLowerCase().split(" ")[0]))
      .map(([name, coords]) => ({
        display: name + ", " + coords.parish + ", Jamaica",
        street: name,
        city: coords.parish,
        lat: coords.lat,
        lng: coords.lng,
        isDropoff,
        isLandmark: true
      }));
    
    let _verifiedPudoResults = [];  // hoisted so outer catch can safely reference (R29 fix)
    try {
      // Phase 2E Scope Y — prepend verified PUDO locations (additive)
      try {
        const _vRes = await fetch(`${API_URL}/api/locations/search?q=${encodeURIComponent(query)}&limit=5`);
        if (_vRes.ok) {
          const _vData = await _vRes.json();
          _verifiedPudoResults = (_vData.results || [])
            .filter(r => r.verified_location || r.source === "pudo_verified")
            .map(r => ({
              display: `${r.name}, ${r.parish || "Jamaica"}`,
              name: r.name,
              lat: r.lat, lng: r.lng,
              city: r.town || r.parish || "",
              parish: r.parish || "Kingston",
              source: "pudo_verified",
              verified_location: true,
            }));
        }
      } catch (_e) { /* non-fatal */ }
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query + ", Jamaica")}&limit=5&addressdetails=1`
      );
      const data = await response.json();
      const apiResults = data.map(item => ({
        display: item.display_name,
        street: item.address?.road || item.name || query,
        city: item.address?.city || item.address?.town || item.address?.village || "",
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lon),
        isDropoff
      }));
      
      // Combine landmarks (first) with API results
      setAddressSuggestions([...(_verifiedPudoResults||[]), ...matchedLandmarks, ...apiResults].slice(0, 8));
    } catch (err) {
      console.error("Address search error:", err);
      // If API fails, still show landmarks
      setAddressSuggestions([...(_verifiedPudoResults||[]), ...matchedLandmarks]);
    }
    setSearchingAddress(false);
  }, []);

  // Select suggestion - ONLY sets GPS coordinates for distance calculation
  // User's typed address is NEVER overwritten
  const selectSuggestion = (suggestion) => {
    if (suggestion.isDropoff) {
      // Only set coordinates - user keeps their typed address
      setDropoffCoords({ lat: suggestion.lat, lng: suggestion.lng });
      // Only fill city if empty
      if (!dropoffCity) setDropoffCity(suggestion.city);
      // Auto-fill parish - check if the suggestion city matches a parish or find from display
      const parishFromCity = parishes.find(p => p.toLowerCase() === suggestion.city?.toLowerCase());
      const matchedParish = parishFromCity || parishes.find(p => suggestion.display.toLowerCase().includes(p.toLowerCase()));
      if (matchedParish) setDropoffParish(matchedParish);
      toast.success("GPS coordinates set for distance calculation!");
    } else {
      // Only set coordinates - user keeps their typed address
      setPickupCoords({ lat: suggestion.lat, lng: suggestion.lng });
      // Only fill city if empty
      if (!pickupCity) setPickupCity(suggestion.city);
      // Auto-fill parish - check if the suggestion city matches a parish or find from display
      const parishFromCity = parishes.find(p => p.toLowerCase() === suggestion.city?.toLowerCase());
      const matchedParish = parishFromCity || parishes.find(p => suggestion.display.toLowerCase().includes(p.toLowerCase()));
      if (matchedParish) setPickupParish(matchedParish);
      toast.success("GPS coordinates set for distance calculation!");
    }
    setAddressSuggestions([]);
  };

  // Calculate distance between pickup and dropoff
  const calculateDistance = useCallback(() => {
    if (!pickupCoords || !dropoffCoords) return null;
    
    const R = 6371; // Earth's radius in km
    const dLat = (dropoffCoords.lat - pickupCoords.lat) * Math.PI / 180;
    const dLon = (dropoffCoords.lng - pickupCoords.lng) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(pickupCoords.lat * Math.PI / 180) * Math.cos(dropoffCoords.lat * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return (R * c).toFixed(1);
  }, [pickupCoords, dropoffCoords]);

  // Location Sharing Functions
  const createLocationShareLink = async () => {
    if (!recipientName) {
      toast.error("Please enter recipient name first");
      return;
    }
    setLocationShareLoading(true);
    try {
      const res = await api.post("/location-share/create", {
        recipient_email: recipientEmail,
        sender_email: senderEmail,
        recipient_name: recipientName,
        sender_name: user?.name || "Sender"
      });
      setLocationShareToken(res.data.token);
      toast.success("Location sharing link created!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create link");
    } finally {
      setLocationShareLoading(false);
    }
  };

  const getShareUrl = () => {
    if (!locationShareToken) return "";
    return `${window.location.origin}/share-location/${locationShareToken}`;
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(getShareUrl());
    toast.success("Link copied to clipboard!");
  };

  const shareToWhatsApp = () => {
    const msg = encodeURIComponent(`Hi ${recipientName}, please share your delivery location: ${getShareUrl()}`);
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent("Share Your Delivery Location - Liantiao");
    const body = encodeURIComponent(`Hi ${recipientName},\n\nPlease share your delivery location: ${getShareUrl()}\n\nThank you!`);
    window.location.href = `mailto:${recipientEmail}?subject=${subject}&body=${body}`;
  };

  const pollReceiverLocation = async () => {
    if (!locationShareToken) return;
    setPollingLocation(true);
    try {
      const res = await api.get(`/location-share/poll/${locationShareToken}`);
      if (res.data.status === "submitted" && res.data.location) {
        setReceiverLocation(res.data.location);
        // Auto-fill dropoff coordinates
        setDropoffCoords({ lat: res.data.location.lat, lng: res.data.location.lng });
        if (res.data.location.address) {
          setDropoffStreet(res.data.location.address.split(",")[0] || res.data.location.address);
        }
        toast.success("Receiver location received!");
      } else {
        toast.info("Waiting for receiver to share location...");
      }
    } catch (e) {
      toast.error("Failed to check location");
    } finally {
      setPollingLocation(false);
    }
  };

    // Calculate quote using unified pricing API
  useEffect(() => {
    const fetchQuote = async () => {
      let dist = calculateDistance();
      
      if (!dist) {
        if (pickupParish && dropoffParish) {
          dist = pickupParish === dropoffParish ? 10 : 25;
        } else {
          dist = 15;
        }
      }
      
      setDistance(dist);
      
      const totalWeight = getTotalWeight();
      const serviceLevel = 'STANDARD'; // Urgent bonus is separate from service level
      
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        // R29 price-mismatch fix: /api/pricing/quote requires distance_km + weight_lb + speed
        const params = new URLSearchParams({
          delivery_mode: 'ADDRESS',
          pricing_context: 'C2C',
          service_level: serviceLevel,
          weight_lbs: totalWeight.toString(),
          weight_lb: totalWeight.toString(),
          distance_km: String(dist),
          speed: (serviceLevel || 'standard').toLowerCase(),
        });
        if (pickupCoords?.lat) params.set('pickup_lat', pickupCoords.lat.toString());
        if (pickupCoords?.lng) params.set('pickup_lng', pickupCoords.lng.toString());
        if (dropoffCoords?.lat) params.set('delivery_lat', dropoffCoords.lat.toString());
        if (dropoffCoords?.lng) params.set('delivery_lng', dropoffCoords.lng.toString());
        
        const response = await fetch(`${API_URL}/api/pricing/quote?${params}`);
        const data = await response.json();
        
        if (data.breakdown) {
          setQuote({
            base_price: data.breakdown.base_cost_jmd || 0,
            distance_price: data.breakdown.distance_cost_jmd || 0,
            weight_surcharge: data.breakdown.weight_overage_cost_jmd || 0,
            weight_lbs: totalWeight,
            peak_surcharge: data.breakdown.peak_surcharge_jmd || 0,
            peak_applied: data.rates?.peak_hours_enabled && data.breakdown.peak_surcharge_jmd > 0,
            peak_enabled: data.rates?.peak_hours_enabled || false,
            peak_percent: data.rates?.peak_hours_surcharge_percent || 30,
            peak_start: data.rates?.peak_hours_start || 16,
            peak_end: data.rates?.peak_hours_end || 20,
            urgent_fee: data.breakdown.speed_multiplier_cost_jmd || 0,
            total_price: data.breakdown.total_cost_jmd || 0,
            distance_km: data.distance?.km || parseFloat(dist),
            is_estimate: !pickupCoords || !dropoffCoords,
            package_count: packages.length + (isMultiPackage ? 0 : 1),
            eta: data.eta?.label || 'Standard Delivery',
            // R30 launch promo overlay metadata
            promo_applied: !!(data.promotion && data.promotion.applied),
            promo_code: data.promotion?.promotion_code || null,
            promo_cell: data.promotion?.matrix_cell || null,
            original_total_price: data.breakdown?.original_total_cost_jmd ?? null
          });
          return;
        }
      } catch (error) {
        console.error('Pricing API error:', error);
      }
      
      // Fallback calculation
      const distancePrice = Math.ceil(parseFloat(dist) * 80);
      const weightSurcharge = Math.ceil(Math.max(0, totalWeight - 10) * 60);
      const totalPrice = 580 + distancePrice + weightSurcharge;
      setQuote({
        base_price: 580,
        distance_price: distancePrice,
        weight_surcharge: weightSurcharge,
        weight_lbs: totalWeight,
        total_price: totalPrice,
        distance_km: dist,
        is_estimate: true,
        package_count: packages.length + (isMultiPackage ? 0 : 1)
      });
    };
    
    clearTimeout(quoteTimerRef.current);
    quoteTimerRef.current = setTimeout(fetchQuote, 500);
    return () => clearTimeout(quoteTimerRef.current);
  }, [packageSize, packageWeight, pickupParish, dropoffParish, pickupCoords, dropoffCoords, calculateDistance, isUrgent]);

  // ── Per-step validators ──────────────────────────────────────────────────
  const validateStep1 = () => {
    const e = {};
    if (pickupMode === "ADDRESS") {
      if (!pickupStreet || pickupStreet.trim().length < 2) e.pickupStreet = "Enter your pickup street address";
      if (!pickupParish) e.pickupParish = "Select your pickup parish";
    }
    if (pickupMode === "PUDO" && !pickupPudoId) e.pickupPudoId = "Select a PUDO drop-off point";
    if (!pickupConfirmed) e.pickupConfirmed = "Please confirm your pickup location on the map";
    return e;
  };

  const validateStep2 = () => {
    const e = {};
    if (!recipientName || recipientName.trim().length < 2) e.recipientName = "Enter the recipient’s full name";
    if (!recipientPhone || recipientPhone.trim().length < 7) e.recipientPhone = "Enter a valid recipient phone number";
    if (dropoffMode === "ADDRESS") {
      if (!dropoffStreet || dropoffStreet.trim().length < 2) e.dropoffStreet = "Enter the delivery street address";
      if (!dropoffParish) e.dropoffParish = "Select the delivery parish";
    }
    if (dropoffMode === "PUDO" && !dropoffPudoId) e.dropoffPudoId = "Select a PUDO collection point";
    if (!dropoffConfirmed) e.dropoffConfirmed = "Please confirm the delivery location on the map";
    return e;
  };

  const validateStep3 = () => {
    const e = {};
    if (!packageWeight || parseFloat(packageWeight) <= 0) e.packageWeight = "Enter the package weight in lbs";
    if (!contentsCategory) e.contentsCategory = "Select a package category";
    if (!packageContents || packageContents.trim().length < 2) e.packageContents = "Briefly describe what’s inside the package";
    if (isUrgent) {
      if (!urgentOfferAmount) e.urgentOfferAmount = `Enter a driver bonus (minimum $${URGENT_MIN} JMD)`;
      else if (parseFloat(urgentOfferAmount) < URGENT_MIN) e.urgentOfferAmount = `Driver bonus must be at least $${URGENT_MIN.toLocaleString()} JMD`;
    }
    return e;
  };

  const handleStepNext = (targetStep, validateFn) => {
    const errors = validateFn();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      // Show the first error as a toast too
      toast.error(Object.values(errors)[0], { icon: "⚠️", duration: 4000 });
      return;
    }
    setStep(targetStep);
  };

  // ========== OPERATOR MODE: Create unpaid package + payment link ==========
  const handleOperatorCheckout = async () => {
    // Validate customer info from step 0
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!customerName || customerName.trim().length < 2) {
      toast.error("Customer name is required"); return;
    }
    if (!customerPhone || customerPhone.trim().length < 7) {
      toast.error("Customer phone is required"); return;
    }
    if (!customerEmail || !emailRegex.test(customerEmail)) {
      toast.error("Valid customer email is required"); return;
    }
    
    // Compliance check
    if (!complianceAcknowledged) {
      toast.error("Please acknowledge the prohibited items policy"); return;
    }
    
    setIsLoading(true);
    try {
      const payload = {
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_email: customerEmail,
        recipient_name: recipientName,
        recipient_phone: recipientPhone,
        recipient_email: recipientEmail || null,
        pickup_address: {
          street: pickupStreet,
          city: pickupCity,
          parish: pickupParish,
          lat: pickupCoords?.lat || null,
          lng: pickupCoords?.lng || null,
          landmark: pickupLandmark || null
        },
        dropoff_address: {
          street: dropoffStreet,
          city: dropoffCity,
          parish: dropoffParish,
          lat: dropoffCoords?.lat || null,
          lng: dropoffCoords?.lng || null,
          landmark: dropoffLandmark || null
        },
        package_size: packageSize,
        package_contents: packageContents,
        contents_category: contentsCategory,
        estimated_weight_lbs: parseFloat(packageWeight) || 5,
        is_fragile: isFragile,
        is_urgent: isUrgent,
        urgent_offer_amount: isUrgent ? parseFloat(urgentOfferAmount) || 0 : null,
        operator_notes: operatorNotes || null
      };
      
      const response = await api.post("/assisted-booking/create", payload);
      
      if (response.data.success) {
        setAssistedBookingResult(response.data);
        // Fetch payment link options for sharing
        const linkResponse = await api.get(`/assisted-booking/payment-link/${response.data.package_id}`);
        setPaymentLinkOptions(linkResponse.data);
        toast.success("Assisted booking created successfully!");
        setStep(7); // Go to operator success screen
      }
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || "Failed to create booking");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle checkout with Stripe
  const handleCheckout = async () => {
    // OPERATOR MODE: Use separate handler
    if (operatorMode) {
      return handleOperatorCheckout();
    }
    
    if (!user) {
      // R29 — open guest checkout gate instead of redirecting to /login
      if (senderEmail && senderEmail.trim()) setGsEmail(senderEmail.trim());
      setGateStep('form');
      setShowGuestGate(true);
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!senderEmail || !senderEmail.trim()) {
      toast.error("Sender email is required");
      return;
    }
    if (!emailRegex.test(senderEmail)) {
      toast.error("Please enter a valid sender email address");
      return;
    }
    
    if (!recipientEmail || !recipientEmail.trim()) {
      toast.error("Receiver email is required");
      return;
    }
    if (!emailRegex.test(recipientEmail)) {
      toast.error("Please enter a valid receiver email address");
      return;
    }
    
    if (!complianceAcknowledged) {
      toast.error("Please acknowledge the prohibited items policy");
      return;
    }
    try {
      // Build packages array for multi-package orders
      let allPackages = [];
      
      if (packages.length > 0) {
        // Multi-package order - use stored packages
        allPackages = packages.map(pkg => ({
          size: pkg.size,
          weight: pkg.weight,
          contents: pkg.contents,
          category: pkg.category,
          photos: pkg.photos,
          is_fragile: pkg.isFragile
        }));
      } else {
        // Single package order
        allPackages = [{
          size: packageSize,
          weight: parseFloat(packageWeight) || 0,
          contents: packageContents,
          category: contentsCategory,
          photos: [packagePhotos.top, packagePhotos.side, packagePhotos.reference].filter(Boolean),
          is_fragile: isFragile
        }];
      }
      
      const totalWeight = getTotalWeight();
      
      const payload = {
        recipient_name: recipientName,
        recipient_phone: recipientPhone,
        recipient_email: recipientEmail,
        pickup_address: {
          street: pickupStreet,
          city: pickupCity,
          parish: pickupParish,
          lat: pickupCoords?.lat || null,
          lng: pickupCoords?.lng || null
        },
        dropoff_address: {
          street: dropoffStreet,
          city: dropoffCity,
          parish: dropoffParish,
          lat: dropoffCoords?.lat || null,
          lng: dropoffCoords?.lng || null
        },
        package_size: packages.length > 0 ? packages[0].size : packageSize,
        package_contents: packages.length > 0 ? packages.map(p => p.contents).filter(Boolean).join("; ") : packageContents,
        package_photos: packages.length > 0 ? packages.flatMap(p => p.photos) : [packagePhotos.top, packagePhotos.side, packagePhotos.reference].filter(Boolean),
        estimated_weight_lbs: totalWeight,
        contents_category: packages.length > 0 ? packages[0].category : contentsCategory,
        prohibited_confirmed: complianceAcknowledged,
        is_urgent: isUrgent,
        is_fragile: packages.length > 0 ? packages.some(p => p.isFragile) : isFragile,
        urgent_offer_amount: isUrgent ? parseFloat(urgentOfferAmount) : null,
        discount_code: (discountCode || "").trim() || null
      };
      const idempotencyKey = `checkout-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      const response = await api.post("/packages/secure-checkout", payload, {
        headers: {
          'Idempotency-Key': idempotencyKey
        }
      });
      
      const payUrl = response.data.checkout_url || response.data.pay_url;
      if (payUrl) {
        // Redirect to Stripe checkout
        window.location.href = payUrl;
        return;
      } else if (response.data.success) {
        // If urgent, create the urgent offer after package is created
        if (isUrgent && response.data.package?.id) {
          try {
            await api.post("/urgent-offers", {
              package_id: response.data.package.id,
              offer_amount: parseFloat(urgentOfferAmount),
              pickup_address: {
                street: pickupStreet,
                city: pickupCity,
                parish: pickupParish
              },
              dropoff_address: {
                street: dropoffStreet,
                city: dropoffCity,
                parish: dropoffParish
              },
              package_size: packageSize,
              sender_name: user?.name,
              recipient_name: recipientName
            });
            toast.success("Urgent offer posted! Drivers can see it now.");
          } catch (urgErr) {
            console.error("Urgent offer creation failed:", urgErr);
            toast.error("Package created but urgent offer failed. Contact support.");
          }
        }
        setCreatedPackage(response.data.package);
        setStep(6);
        toast.success("Package booked successfully!");
      }
    } catch (error) {
      // R29: also surface the {success:false, error:{message}} shape used by the api middleware
      const msg =
        error?.response?.data?.error?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        "Failed to create booking";
      toast.error(msg);
      console.error("Checkout error:", error?.response?.status, error?.response?.data);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    switch(step) {
      // Step 0: OPERATOR MODE - Customer Information (only shown in operatorMode)
      case 0:
        if (!operatorMode) return null;
        return (
          <div className="space-y-5">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Shield className="h-8 w-8 text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-white">Customer Information</h2>
              <p className="text-gray-400 text-sm">Enter the customer's details (booking on their behalf)</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-gray-300">Customer Name *</Label>
                <Input
                  value={customerName}
                  onChange={(e) => { setCustomerName(e.target.value); clearErr("customerName"); }}
                  placeholder="Full name of the customer"
                  className="bg-gray-800 border-gray-700 text-white"
                  data-testid="customer-name-input"
                />
                <FieldError name="customerName" />
              </div>
              
              <div>
                <Label className="text-sm text-gray-300">Customer Phone *</Label>
                <Input
                  value={customerPhone}
                  onChange={(e) => { setCustomerPhone(e.target.value); clearErr("customerPhone"); }}
                  placeholder="876-XXX-XXXX"
                  className="bg-gray-800 border-gray-700 text-white"
                  data-testid="customer-phone-input"
                />
                <FieldError name="customerPhone" />
              </div>
              
              <div>
                <Label className="text-sm text-gray-300">Customer Email * (payment link will be sent here)</Label>
                <Input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => { setCustomerEmail(e.target.value); clearErr("customerEmail"); }}
                  placeholder="customer@email.com"
                  className="bg-gray-800 border-gray-700 text-white"
                  data-testid="customer-email-input"
                />
                <FieldError name="customerEmail" />
              </div>
              
              <div>
                <Label className="text-sm text-gray-300">Operator Notes (internal)</Label>
                <textarea
                  value={operatorNotes}
                  onChange={(e) => setOperatorNotes(e.target.value)}
                  placeholder="Any internal notes about this booking..."
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                  rows={2}
                  data-testid="operator-notes-input"
                />
              </div>
            </div>
            
            <div className="flex justify-end pt-4">
              <button
                type="button"
                onClick={() => {
                  const e = {};
                  if (!customerName || customerName.trim().length < 2) e.customerName = "Customer name is required";
                  if (!customerPhone || customerPhone.trim().length < 7) e.customerPhone = "Valid phone number required";
                  if (!customerEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail)) e.customerEmail = "Valid email required";
                  setFieldErrors(e);
                  if (Object.keys(e).length > 0) {
                    toast.error(Object.values(e)[0]);
                    return;
                  }
                  setStep(1);
                }}
                className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-xl"
                data-testid="step0-next-btn"
              >
                Continue to Pickup Location
              </button>
            </div>
          </div>
        );

      // Step 1: Pickup Location
      case 1:
        return (
          <div className="space-y-5">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <MapPin className="h-8 w-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-white">Pickup Location</h2>
              <p className="text-gray-400 text-sm">Where should we collect the package?</p>
            </div>

            {/* Pickup Mode Toggle - Address vs PUDO */}
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setPickupMode("ADDRESS")}
                className={`flex-1 p-3 rounded-xl border-2 flex items-center justify-center gap-2 transition-all ${
                  pickupMode === "ADDRESS" 
                    ? "border-emerald-500 bg-emerald-500/20 text-emerald-400" 
                    : "border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600"
                }`}
                data-testid="pickup-mode-address"
              >
                <Home className="h-4 w-4" />
                <span className="text-sm font-medium">From My Address</span>
              </button>
              <button
                type="button"
                onClick={() => setPickupMode("PUDO")}
                className={`flex-1 p-3 rounded-xl border-2 flex items-center justify-center gap-2 transition-all ${
                  pickupMode === "PUDO" 
                    ? "border-amber-500 bg-amber-500/20 text-amber-400" 
                    : "border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600"
                }`}
                data-testid="pickup-mode-pudo"
              >
                <Building2 className="h-4 w-4" />
                <span className="text-sm font-medium">Drop at PUDO</span>
              </button>
            </div>
            {pickupMode === "PUDO" && (
              <p className="text-amber-400 text-xs text-center mb-4">
                💡 You will bring the package to the selected PUDO location
              </p>
            )}

            {/* PUDO Selection for Pickup - Enhanced with Parish Filter & Search */}
            {pickupMode === "PUDO" && (
              <div className="mb-4">
                <PudoSelector
                  pudos={pudos}
                  selectedPudoId={pickupPudoId}
                  onSelect={handlePickupPudoSelect}
                  loading={loadingPudos}
                  label="Select PUDO Drop-off Location *"
                  placeholder="Select where you'll drop off the package"
                  darkMode={true}
                  testIdPrefix="pickup-pudo"
                />
              </div>
            )}

            {/* Show address fields only for ADDRESS mode */}
            {pickupMode === "ADDRESS" && (
              <>
            {/* GPS Checkbox */}
            <div className="flex items-center justify-between p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
              <div className="flex items-center gap-3">
                <Locate className="h-5 w-5 text-emerald-500" />
                <div>
                  <p className="text-white font-medium">Use Current Location</p>
                  <p className="text-gray-400 text-xs">Auto-fill address from GPS</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {gettingLocation && <Loader2 className="h-4 w-4 animate-spin text-emerald-500" />}
                <Checkbox 
                  checked={useGPS}
                  onCheckedChange={(checked) => {
                    setUseGPS(checked);
                    if (checked) getCurrentLocation();
                  }}
                  className="border-emerald-500 data-[state=checked]:bg-emerald-500"
                />
              </div>
            </div>
            
            {pickupCoords && (
              <div className="text-xs text-emerald-400 text-center">
                📍 Coordinates: {pickupCoords.lat.toFixed(4)}, {pickupCoords.lng.toFixed(4)}
              </div>
            )}
            
            
            {/* Saved Pickup Locations */}
            {savedPickupLocations.length > 0 && (
              <div className="space-y-2 mb-4">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Star className="h-4 w-4 text-amber-500" /> Quick Select Saved Pickup
                </Label>
                <select
                  className="w-full p-3 rounded-xl bg-gray-800 border border-gray-700 text-white"
                  onChange={(e) => {
                    const loc = savedPickupLocations.find(l => l.id === e.target.value);
                    if (loc) applySavedPickup(loc);
                  }}
                  defaultValue=""
                >
                  <option value="">-- Select saved location --</option>
                  {savedPickupLocations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.label}</option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Address Fields */}
            <div className="space-y-3">
              <div className="relative">
                <Label className="text-gray-300">Street Address *</Label>
                <div className="relative">
                  <Input 
                    placeholder="Street address" 
                    value={pickupStreet} 
                    onChange={(e) => {
                      setPickupStreet(e.target.value);
                      clearErr('pickupStreet');
                      clearTimeout(searchTimerRef.current);
                      if (e.target.value.length >= 3) {
                        searchTimerRef.current = setTimeout(() => searchAddress(e.target.value, false), 400);
                      } else { setAddressSuggestions([]); }
                    }}
                    className="h-12 rounded-xl bg-gray-800 border-gray-700 text-white pr-24"
                  />
                  {searchingAddress && <Loader2 className="absolute right-3 top-3 h-5 w-5 animate-spin text-gray-400" />}
                  <FieldError name="pickupStreet" />
                  {!searchingAddress && pickupStreet.length >= 3 && (
                    <button
                      type="button"
                      onClick={() => searchAddress(pickupStreet, false)}
                      className="absolute right-2 top-2 px-2 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
                    >
                      Find GPS
                    </button>
                  )}
                </div>
                
                {/* Address Suggestions */}
                {addressSuggestions.length > 0 && !addressSuggestions[0]?.isDropoff && (
                  <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                    {addressSuggestions.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => selectSuggestion(s)}
                        className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                      >
                        <MapPin className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                        <span className="truncate">{s.display}</span>
                        {(s.verified_location || s.source === "pudo_verified") && (
                          <span data-testid="verified-location-badge" className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 whitespace-nowrap">Verified Location</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-300">City</Label>
                  <Input 
                    placeholder="City or town" 
                    value={pickupCity} 
                    onChange={(e) => setPickupCity(e.target.value)}
                    className="h-12 rounded-xl bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Parish *</Label>
                  <FieldError name="pickupParish" />
                  <Select value={pickupParish} onValueChange={(v) => { setPickupParish(v); clearErr("pickupParish"); }}>
                    <SelectTrigger className="h-12 rounded-xl bg-gray-800 border-gray-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {parishes.map(p => <SelectItem key={p} value={p} className="text-white">{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
              </>
            )}
            
            {/* Confirm Checkbox - shown for both ADDRESS and PUDO modes */}
            <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-xl">
              <FieldError name="pickupConfirmed" />
              <Checkbox 
                checked={pickupConfirmed}
                onCheckedChange={setPickupConfirmed}
                className="border-emerald-500 data-[state=checked]:bg-emerald-500"
              />
              <span className="text-gray-300 text-sm">
                {pickupMode === "PUDO" 
                  ? "I confirm I will drop off the package at the selected PUDO"
                  : "I confirm this pickup address is correct"
                }
              </span>
            </div>
            
            {/* Save Pickup Location */}
            {user && (
              <div className="flex items-center gap-3 p-3 bg-amber-900/20 rounded-xl border border-amber-700/30">
                <Checkbox 
                  checked={wantSavePickup}
                  onCheckedChange={setWantSavePickup}
                  className="border-amber-500 data-[state=checked]:bg-amber-500"
                />
                <span className="text-gray-300 text-sm flex items-center gap-2">
                  <Star className="h-4 w-4 text-amber-500" />
                  Save this pickup address for quick select
                </span>
              </div>
            )}
            
            {/* Validation summary for step 1 */}
            {Object.keys(fieldErrors).length > 0 && (
              <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 space-y-1">
                <p className="text-red-400 text-sm font-semibold flex items-center gap-2">&#9888; Please fix the following:</p>
                {Object.values(fieldErrors).map((msg, i) => (
                  <p key={i} className="text-red-300 text-xs flex items-center gap-1">&#8226; {msg}</p>
                ))}
              </div>
            )}
            <Button 
              onClick={() => handleStepNext(2, validateStep1)}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl h-12"
              data-testid="continue-to-dropoff-btn"
            >
              Continue to Dropoff
            </Button>
          </div>
        );
      
      // Step 2: Dropoff Location
      case 2:
        return (
          <div className="space-y-5">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Navigation className="h-8 w-8 text-purple-600" />
              </div>
              <h2 className="text-xl font-bold text-white">Dropoff Location</h2>
              <p className="text-gray-400 text-sm">Where should we deliver the package?</p>
            </div>
            
            
            {/* Saved Delivery Locations */}
            {savedDeliveryLocations.length > 0 && (
              <div className="space-y-2 mb-4">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Star className="h-4 w-4 text-amber-500" /> Quick Select Saved Delivery
                </Label>
                <select
                  className="w-full p-3 rounded-xl bg-gray-800 border border-gray-700 text-white"
                  onChange={(e) => {
                    const loc = savedDeliveryLocations.find(l => l.id === e.target.value);
                    if (loc) applySavedDelivery(loc);
                  }}
                  defaultValue=""
                >
                  <option value="">-- Select saved location --</option>
                  {savedDeliveryLocations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.label} - {loc.recipient_name}</option>
                  ))}
                </select>
              </div>
            )}
            

            {/* Sender Email */}
            <div className="space-y-3 p-4 bg-emerald-900/20 rounded-xl border border-emerald-700/50">
              <h3 className="text-white font-medium flex items-center gap-2">
                <Mail className="h-4 w-4 text-emerald-500" />
                Sender Information
              </h3>
              <Input 
                placeholder="Your email" 
                type="email"
                value={senderEmail} 
                onChange={(e) => setSenderEmail(e.target.value)}
                className="h-11 rounded-lg bg-gray-800 border-emerald-700/50 text-white"
              />
              <p className="text-xs text-gray-400">Confirmation and tracking info will be sent to this email</p>
            </div>
            {/* Recipient Details */}
            <div className="space-y-3 p-4 bg-gray-800/50 rounded-xl border border-gray-700">
              <h3 className="text-white font-medium flex items-center gap-2">
                <User className="h-4 w-4 text-purple-500" />
                Recipient Details
              </h3>
              <Input 
                placeholder="Recipient name" 
                value={recipientName} 
                onChange={(e) => { setRecipientName(e.target.value); clearErr("recipientName"); }}
                className="h-11 rounded-lg bg-gray-800 border-gray-700 text-white"
              />
                  <FieldError name="recipientName" />
              <div className="grid grid-cols-2 gap-2">
                <Input 
                  placeholder="876-000-0000" 
                  type="tel"
                  value={recipientPhone} 
                  onChange={(e) => { setRecipientPhone(e.target.value); clearErr("recipientPhone"); }}
                  className="h-11 rounded-lg bg-gray-800 border-gray-700 text-white"
                />
                  <FieldError name="recipientPhone" />
                <Input 
                  placeholder="Receiver email" 
                  type="email"
                  value={recipientEmail} 
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  className="h-11 rounded-lg bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>
            
            {/* Location Sharing Feature */}
            <div className="p-4 bg-gradient-to-r from-green-900/30 to-amber-900/30 rounded-xl border border-green-700/50">
              <div className="flex items-center gap-2 mb-3">
                <Link2 className="h-5 w-5 text-amber-400" />
                <h3 className="text-white font-medium">Get Precise Delivery Location</h3>
              </div>
              <p className="text-gray-400 text-sm mb-4">
                Send a link to the receiver so they can share their exact GPS location for accurate delivery.
              </p>
              
              {!locationShareToken ? (
                <Button
                  onClick={createLocationShareLink}
                  disabled={locationShareLoading || !recipientName}
                  className="w-full bg-green-600 hover:bg-green-700 text-white rounded-xl h-11"
                  data-testid="create-location-link-btn"
                >
                  {locationShareLoading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating Link...</>
                  ) : (
                    <><Link2 className="h-4 w-4 mr-2" /> Generate Location Link</>
                  )}
                </Button>
              ) : (
                <div className="space-y-3">
                  {/* Link Display */}
                  <div className="flex items-center gap-2 p-2 bg-gray-800 rounded-lg">
                    <input 
                      readOnly 
                      value={getShareUrl()} 
                      className="flex-1 bg-transparent text-sm text-gray-300 outline-none truncate"
                    />
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={copyShareLink}
                      className="text-amber-400 hover:text-amber-300"
                      data-testid="copy-location-link-btn"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {/* Share Buttons */}
                  <div className="flex gap-2">
                    <Button
                      onClick={shareToWhatsApp}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white rounded-lg h-10"
                      data-testid="share-whatsapp-btn"
                    >
                      <MessageCircle className="h-4 w-4 mr-1" /> WhatsApp
                    </Button>
                    <Button
                      onClick={shareViaEmail}
                      variant="outline"
                      className="flex-1 border-amber-500 text-amber-400 hover:bg-amber-500/10 rounded-lg h-10"
                      data-testid="share-email-btn"
                    >
                      <Mail className="h-4 w-4 mr-1" /> Email
                    </Button>
                  </div>
                  
                  {/* Poll / Status */}
                  <div className="flex items-center justify-between">
                    <Button
                      onClick={pollReceiverLocation}
                      disabled={pollingLocation}
                      variant="ghost"
                      className="text-gray-400 hover:text-white text-sm"
                      data-testid="poll-location-btn"
                    >
                      {pollingLocation ? (
                        <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Checking...</>
                      ) : (
                        <><Navigation className="h-4 w-4 mr-1" /> Check for Location</>
                      )}
                    </Button>
                    {receiverLocation && (
                      <span className="text-green-400 text-sm flex items-center gap-1">
                        <CheckCircle className="h-4 w-4" /> Location Received
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* PUDO vs Address Mode Toggle */}
            <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30">
              <Label className="text-white font-medium mb-3 block">Delivery Option</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => { setDropoffMode("ADDRESS"); setDropoffPudoId(""); }}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    dropoffMode === "ADDRESS" 
                      ? "border-emerald-500 bg-emerald-500/20 text-white" 
                      : "border-gray-600 bg-gray-800 text-gray-400 hover:border-gray-500"
                  }`}
                  data-testid="delivery-mode-address"
                >
                  <Home className="h-5 w-5 mx-auto mb-1" />
                  <span className="text-sm font-medium">To Address</span>
                </button>
                <button
                  type="button"
                  onClick={() => setDropoffMode("PUDO")}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    dropoffMode === "PUDO" 
                      ? "border-cyan-500 bg-cyan-500/20 text-white" 
                      : "border-gray-600 bg-gray-800 text-gray-400 hover:border-gray-500"
                  }`}
                  data-testid="delivery-mode-pudo"
                >
                  <Building2 className="h-5 w-5 mx-auto mb-1" />
                  <span className="text-sm font-medium">To PUDO Point</span>
                </button>
              </div>
              {dropoffMode === "PUDO" && (
                <p className="text-xs text-cyan-400 mt-2">Pick Up Drop Off point - receiver collects from location</p>
              )}
            </div>

            {/* PUDO Selection - Enhanced with Parish Filter & Search */}
            {dropoffMode === "PUDO" && (
              <div className="space-y-3">
                <PudoSelector
                  pudos={pudos}
                  selectedPudoId={dropoffPudoId}
                  onSelect={handlePudoSelect}
                  loading={loadingPudos}
                  label="Select PUDO Delivery Location *"
                  placeholder="Select where receiver will pick up package"
                  darkMode={true}
                  testIdPrefix="dropoff-pudo"
                />
              </div>
            )}
            
            {/* Address Fields - Only show when ADDRESS mode */}
            {dropoffMode === "ADDRESS" && (
            <div className="space-y-3">
              <div className="relative">
                <Label className="text-gray-300">Delivery Address *</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <Input 
                    placeholder="Delivery address" 
                    value={dropoffStreet} 
                    onChange={(e) => {
                      setDropoffStreet(e.target.value);
                      clearErr('dropoffStreet');
                      clearTimeout(searchTimerRef.current);
                      if (e.target.value.length >= 3) {
                        searchTimerRef.current = setTimeout(() => searchAddress(e.target.value, true), 400);
                      } else { setAddressSuggestions([]); }
                    }}
                    className="h-12 rounded-xl bg-gray-800 border-gray-700 text-white pl-10 pr-24"
                  />
                  <FieldError name="dropoffStreet" />
                  {!searchingAddress && dropoffStreet.length >= 3 && (
                    <button
                      type="button"
                      onClick={() => searchAddress(dropoffStreet, true)}
                      className="absolute right-2 top-2 px-2 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
                    >
                      Find GPS
                    </button>
                  )}
                  {searchingAddress && <Loader2 className="absolute right-3 top-3 h-5 w-5 animate-spin text-gray-400" />}
                </div>
                
                {/* Address Suggestions */}
                {addressSuggestions.length > 0 && addressSuggestions[0]?.isDropoff && (
                  <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                    {addressSuggestions.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => selectSuggestion(s)}
                        className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                      >
                        <MapPin className="h-4 w-4 text-purple-500 flex-shrink-0" />
                        <span className="truncate">{s.display}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-300">City</Label>
                  <Input 
                    placeholder="City or town" 
                    value={dropoffCity} 
                    onChange={(e) => setDropoffCity(e.target.value)}
                    className="h-11 rounded-lg bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Parish *</Label>
                  <FieldError name="dropoffParish" />
                  <Select value={dropoffParish} onValueChange={(v) => { setDropoffParish(v); clearErr("dropoffParish"); }}>
                    <SelectTrigger className="h-11 rounded-lg bg-gray-800 border-gray-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {parishes.map(p => <SelectItem key={p} value={p} className="text-white">{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            )}
            
            {/* Distance Preview */}
            {distance && (
              (pickupCoords && dropoffCoords) ? (
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl text-center" data-testid="distance-exact">
                  <p className="text-blue-400 text-sm">
                    📏 Distance: <span className="font-bold">{distance} km</span>
                    <span className="text-blue-300/70"> — from map location</span>
                  </p>
                </div>
              ) : (
                <div className="p-3 bg-amber-500/10 border border-amber-500/40 rounded-xl" data-testid="distance-estimate">
                  <p className="text-amber-400 text-sm font-semibold text-center">
                    📏 Estimated Distance: <span className="font-bold">{distance} km</span> (based on parish)
                  </p>
                  <p className="text-amber-300/80 text-xs text-center mt-1">
                    ⚠️ No map pin set yet — this price is an estimate. Type your street above and pick your address (or tap “Find GPS”) to lock in the exact price.
                  </p>
                </div>
              )
            )}
            
            {/* Dropoff Landmark - Optional */}
            <div>
              <Label className="text-gray-400 text-sm">Landmark <span className="text-gray-600">(optional - helps driver find location)</span></Label>
              <Input 
                placeholder="Nearest landmark"
                value={dropoffLandmark}
                onChange={(e) => setDropoffLandmark(e.target.value)}
                className="bg-gray-800 border-gray-700 h-12 rounded-xl mt-1"
              />
            </div>
            
            {/* Confirm Checkbox */}
            <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-xl">
              <Checkbox 
                checked={dropoffConfirmed}
                onCheckedChange={setDropoffConfirmed}
                className="border-purple-500 data-[state=checked]:bg-purple-500"
              />
              <span className="text-gray-300 text-sm">I confirm this delivery address is correct</span>
            </div>
            
            {/* Save Delivery Location */}
            {user && (
              <div className="flex items-center gap-3 p-3 bg-amber-900/20 rounded-xl border border-amber-700/30">
                <Checkbox 
                  checked={wantSaveDelivery}
                  onCheckedChange={setWantSaveDelivery}
                  className="border-amber-500 data-[state=checked]:bg-amber-500"
                />
                <span className="text-gray-300 text-sm flex items-center gap-2">
                  <Star className="h-4 w-4 text-amber-500" />
                  Save this delivery address for quick select
                </span>
              </div>
            )}
            
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => { setStep(1); setFieldErrors({}); }} className="flex-1 h-12 rounded-xl border-gray-700 text-gray-300">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
              {/* Validation summary for step 2 */}
              {Object.keys(fieldErrors).length > 0 && (
                <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 space-y-1 w-full">
                  <p className="text-red-400 text-sm font-semibold flex items-center gap-2">&#9888; Please fix the following:</p>
                  {Object.values(fieldErrors).map((msg, i) => (
                    <p key={i} className="text-red-300 text-xs flex items-center gap-1">&#8226; {msg}</p>
                  ))}
                </div>
              )}
              <Button 
                onClick={() => handleStepNext(3, validateStep2)}
                className="flex-1 bg-purple-500 hover:bg-purple-600 text-white rounded-xl h-12"
              >
                Continue
              </Button>
            </div>
          </div>
        );
      
      // Step 3: Package Details
      case 3:
        return (
          <div className="space-y-5">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Package className="h-8 w-8 text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-white">Package Details</h2>
              <p className="text-gray-400 text-sm">Select size and describe contents</p>
            </div>
            
            {/* Package Size */}
            <div className="space-y-3">
              <Label className="text-gray-300">Package Size *</Label>
              <div className="grid grid-cols-2 gap-3">
                {PACKAGE_SIZES.map(size => (
                  <button
                    key={size.value}
                    onClick={() => setPackageSize(size.value)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      packageSize === size.value 
                        ? 'border-amber-500 bg-amber-500/10' 
                        : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                    }`}
                  >
                    <p className="text-white font-bold">{size.label}</p>
                    <p className="text-gray-400 text-xs">{size.desc}</p>
                    <p className="text-amber-500 font-bold mt-1">${size.price.toLocaleString()}</p>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Package Weight */}
            <div>
              <Label className="text-gray-300">Package Weight (lbs) *</Label>
              <Input
                type="number"
                min="0.1"
                step="0.1"
                placeholder="Weight in lbs"
                value={packageWeight}
                onChange={(e) => { setPackageWeight(e.target.value); clearErr("packageWeight"); }}
                className="h-12 rounded-xl bg-gray-800 border-gray-700 text-white"
              />
                    <FieldError name="packageWeight" />
              <p className="text-xs text-gray-500 mt-1">
                📦 Up to 10 lbs included in base rate. Over 10 lbs: +$60 JMD per additional lb
              </p>
            </div>
            
            {/* Contents Category */}
            <div>
              <Label className="text-gray-300">What's Inside? *</Label>
              <Select value={contentsCategory} onValueChange={setContentsCategory}>
                <SelectTrigger className="h-12 rounded-xl bg-gray-800 border-gray-700 text-white">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  {CONTENT_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value} className="text-white">{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Description */}
            <div>
              <Label className="text-gray-300">Package Description</Label>
              <Textarea 
                placeholder="What are you sending?"
                value={packageContents}
                onChange={(e) => { setPackageContents(e.target.value); clearErr("packageContents"); }}
                className="min-h-[80px] rounded-xl bg-gray-800 border-gray-700 text-white"
              />
            </div>
            

            {/* FRAGILE PACKAGE OPTION */}
            <div className={`p-4 rounded-xl border-2 transition-all ${isFragile ? "border-red-500 bg-red-500/10" : "border-gray-700 bg-gray-800/50"}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isFragile ? "bg-red-500/20" : "bg-gray-700"}`}>
                    <AlertTriangle className={`h-5 w-5 ${isFragile ? "text-red-400" : "text-gray-400"}`} />
                  </div>
                  <div>
                    <p className="text-white font-bold">Fragile Package</p>
                    <p className="text-gray-400 text-xs">Print FRAGILE warning on shipping label</p>
                  </div>
                </div>
                <Switch checked={isFragile} onCheckedChange={setIsFragile} data-testid="fragile-toggle" />
              </div>
            </div>
            
            {/* ESTIMATED PRICE WARNING */}
            <div className="p-4 bg-amber-500/10 border border-amber-500/50 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-400 font-bold">Price is Estimated</p>
                  <p className="text-gray-400 text-sm">The weight you enter is an estimate. Final delivery cost may change after driver verifies the package weight and size at pickup.</p>
                </div>
              </div>
            </div>
            
            {/* PACKAGE PHOTOS - 1 Required, up to 3 */}
            <div className="space-y-3">
              <Label className="text-gray-300 flex items-center gap-2">
                <Camera className="h-4 w-4" /> Package Photos ({photoCount}/3) <span className="text-amber-500">*min 1</span>
              </Label>
              <p className="text-xs text-gray-500">Take at least 1 photo. Optional: Top view, Side view, or reference for size</p>
              
              <div className="grid grid-cols-3 gap-3">
                {["top", "side", "reference"].map((type, idx) => (
                  <div key={type} className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => handlePhotoUpload(type, e.target.files[0])}
                      className="hidden"
                      id={`photo-${type}`}
                    />
                    <label
                      htmlFor={`photo-${type}`}
                      className={`flex flex-col items-center justify-center p-3 h-24 rounded-xl border-2 border-dashed cursor-pointer transition-all ${
                        packagePhotos[type]
                          ? "border-emerald-500 bg-emerald-500/10"
                          : idx === 0 && !hasMinimumPhotos
                            ? "border-amber-500 bg-amber-500/5 hover:border-amber-400"
                            : "border-gray-600 bg-gray-800 hover:border-gray-500"
                      }`}
                    >
                      {packagePhotos[type] ? (
                        <CheckCircle className="h-6 w-6 text-emerald-500" />
                      ) : (
                        <ImagePlus className="h-6 w-6 text-gray-500" />
                      )}
                      <span className="text-xs text-gray-400 mt-1 capitalize">
                        {type} {idx === 0 && !hasMinimumPhotos && <span className="text-amber-500">*</span>}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
              
              {photoUploading && (
                <div className="flex items-center gap-2 text-amber-500 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" /> Uploading photo...
                </div>
              )}
            </div>
            

            

            
            {/* URGENT DELIVERY OPTION */}
            <div className={`p-4 rounded-xl border-2 transition-all ${isUrgent ? 'border-red-500 bg-red-500/10' : 'border-gray-700 bg-gray-800/50'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isUrgent ? 'bg-red-500/20' : 'bg-gray-700'}`}>
                    <Truck className={`h-5 w-5 ${isUrgent ? 'text-red-400' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <p className="text-white font-bold">Urgent Delivery</p>
                    <p className="text-gray-400 text-xs">Make an offer to drivers for priority pickup</p>
                  </div>
                </div>
                <Switch checked={isUrgent} onCheckedChange={setIsUrgent} data-testid="urgent-toggle" />
              </div>
              
              {isUrgent && (
                <div className="space-y-3 mt-4 pt-4 border-t border-red-500/30">
                  <div className={`p-3 rounded-lg text-center font-bold text-lg ${
                    parseFloat(urgentOfferAmount) >= URGENT_HOT 
                      ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' 
                      : parseFloat(urgentOfferAmount) >= URGENT_MIN
                        ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                        : 'bg-gray-700/50 text-gray-400'
                  }`} data-testid="urgent-label">
                    {parseFloat(urgentOfferAmount) >= URGENT_HOT 
                      ? 'Hot Package Alert!' 
                      : parseFloat(urgentOfferAmount) >= URGENT_MIN 
                        ? 'Rush Dis!' 
                        : 'Set your offer below'}
                  </div>
                  
                  <div>
                    <Label className="text-gray-300">Your Offer to Drivers (J$) *</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">J$</span>
                      <Input
                        type="number"
                        min={URGENT_MIN}
                        step="100"
                        placeholder={`Min ${URGENT_MIN.toLocaleString()}`}
                        value={urgentOfferAmount}
                        onChange={(e) => { setUrgentOfferAmount(e.target.value); clearErr("urgentOfferAmount"); }}
                        className="h-12 pl-10 rounded-xl bg-gray-800 border-gray-700 text-white text-lg font-bold"
                        data-testid="urgent-offer-input"
                      />
                      <FieldError name="urgentOfferAmount" />
                    </div>
                    {urgentOfferAmount && parseFloat(urgentOfferAmount) < URGENT_MIN && (
                      <p className="text-red-400 text-xs mt-1">Minimum offer is J${URGENT_MIN.toLocaleString()}</p>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-4 gap-2">
                    {[800, 1500, 3000, 5000].map(amt => (
                      <button
                        key={amt}
                        onClick={() => setUrgentOfferAmount(String(amt))}
                        className={`p-2 rounded-lg border text-center font-bold text-sm transition-all ${
                          String(amt) === urgentOfferAmount
                            ? 'border-red-400 bg-red-500/20 text-red-400'
                            : 'border-gray-600 bg-gray-800 text-gray-300 hover:border-gray-500'
                        }`}
                        data-testid={`urgent-preset-${amt}`}
                      >
                        ${amt.toLocaleString()}
                      </button>
                    ))}
                  </div>
                  
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-amber-400 text-xs font-medium flex items-center gap-1">
                      <Star className="h-3 w-3" /> Higher offers get picked up faster! This bonus is added on top of the standard shipping fee.
                    </p>
                    <p className="text-gray-500 text-xs mt-1">
                      Driver has 25 minutes to pick up after accepting.
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Quote Preview */}
            {quote && (
              <div className="p-4 bg-gradient-to-r from-emerald-500/10 to-amber-500/10 border border-emerald-500/30 rounded-xl">
                <h3 className="text-white font-bold mb-2">Shipping Quote</h3>
                {quote.is_estimate && (
                  <div data-testid="quote-estimate-note" className="flex items-start gap-2 mb-2 px-2.5 py-2 rounded-lg bg-amber-500/15 border border-amber-500/40 text-xs">
                    <span>⚠️</span>
                    <span className="text-amber-200">
                      Estimated price — distance is based on the parish because no map pin was set.
                      Pick your exact address to confirm the final price and avoid surprises at payment.
                    </span>
                  </div>
                )}
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between text-gray-400">
                    <span>Base Rate</span>
                    <span>${quote.base_price?.toLocaleString()} JMD</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>Distance ({distance} km{quote.is_estimate ? ', est.' : ''})</span>
                    <span>${quote.distance_price?.toLocaleString()} JMD</span>
                  </div>
                  {quote.weight_surcharge > 0 && (
                    <div className="flex justify-between text-gray-400">
                      <span>Weight overage ({(quote.weight_lbs - 10).toFixed(1)} lbs over included)</span>
                      <span>${quote.weight_surcharge?.toLocaleString()} JMD</span>
                    </div>
                  )}
                  {quote.weight_lbs > 0 && quote.weight_lbs <= 10 && (
                    <div className="flex justify-between text-gray-400">
                      <span>Weight ({quote.weight_lbs} lbs - included)</span>
                      <span>$0 JMD</span>
                    </div>
                  )}
                  {/* Peak-hour surcharge — shown when active */}
                  {quote.peak_applied && quote.peak_surcharge > 0 && (
                    <div className="flex justify-between items-center text-amber-400 font-semibold">
                      <span className="flex items-center gap-1">
                        <span className="inline-block peak-flash-icon">&#9889;</span>
                        Peak Hour Surcharge (+{quote.peak_percent}%)
                      </span>
                      <span>+${quote.peak_surcharge?.toLocaleString()} JMD</span>
                    </div>
                  )}
                  {/* Heads-up: peak is enabled but window not started yet */}
                  {quote.peak_enabled && !quote.peak_applied && (
                    <div className="flex items-start gap-2 text-amber-500/80 text-xs mt-1 px-1 py-1 rounded bg-amber-500/10">
                      <span>&#9888;&#65039;</span>
                      <span>
                        Peak pricing (+{quote.peak_percent}%) applies{" "}
                        {(() => {
                          const fmt = h => `${h % 12 || 12}:00 ${h < 12 ? "AM" : "PM"}`;
                          return `${fmt(quote.peak_start)}–${fmt(quote.peak_end)}`;
                        })()}.
                        Book now to avoid the surcharge.
                      </span>
                    </div>
                  )}
                  {isUrgent && urgentOfferAmount && parseFloat(urgentOfferAmount) >= URGENT_MIN && (
                    <div className="flex justify-between text-red-400 font-medium">
                      <span>Urgent Driver Bonus</span>
                      <span>${parseFloat(urgentOfferAmount).toLocaleString()} JMD</span>
                    </div>
                  )}
                  {quote.promo_applied && (
                    <div data-testid="promo-applied-callout" className="flex items-center justify-between rounded-md bg-amber-500/15 border border-amber-500/40 px-2.5 py-1.5 text-xs">
                      <span className="text-amber-300 font-semibold uppercase tracking-wide">Launch Special applied</span>
                      {quote.original_total_price ? (
                        <span className="text-amber-100/70 line-through">
                          ${Number(quote.original_total_price).toLocaleString()} JMD
                        </span>
                      ) : null}
                    </div>
                  )}
                  <div className="flex justify-between text-white font-bold text-lg pt-2 border-t border-gray-700">
                    <span>Total</span>
                    <span className="text-emerald-400" data-testid="quote-total">
                      ${((quote.total_price || 0) + (isUrgent ? (parseFloat(urgentOfferAmount) || 0) : 0)).toLocaleString()} JMD
                    </span>
                  </div>
                  <div className="pt-3 mt-1 border-t border-gray-700 space-y-2" data-testid="discount-section">
                    <label className="text-xs text-gray-400">Have a referral or discount code?</label>
                    <div className="flex gap-2">
                      <input
                        value={discountCode}
                        onChange={(e) => { setDiscountCode(e.target.value.toUpperCase()); setDiscountError(""); setAppliedDiscount(null); }}
                        placeholder="Enter code"
                        data-testid="discount-code-input"
                        className="flex-1 px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm placeholder:text-gray-500 focus:border-emerald-500 focus:outline-none"
                      />
                      <Button type="button" onClick={applyDiscount} disabled={applyingDiscount} data-testid="apply-discount-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-4">
                        {applyingDiscount ? "..." : "Apply"}
                      </Button>
                    </div>
                    {discountError && <p className="text-red-400 text-xs" data-testid="discount-error">{discountError}</p>}
                    {appliedDiscount && (
                      <div className="text-sm space-y-1" data-testid="discount-applied">
                        <div className="flex justify-between text-emerald-400">
                          <span>{appliedDiscount.label} ({appliedDiscount.discount_percent}% off)</span>
                          <span>-${Math.round(appliedDiscount.discount_amount).toLocaleString()} JMD</span>
                        </div>
                        <div className="flex justify-between text-white font-bold">
                          <span>You pay</span>
                          <span data-testid="discount-new-total">${Math.max(0, Math.round(((quote.total_price || 0) + (isUrgent ? (parseFloat(urgentOfferAmount) || 0) : 0)) - appliedDiscount.discount_amount)).toLocaleString()} JMD</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => { setStep(2); setFieldErrors({}); }} className="flex-1 h-12 rounded-xl border-gray-700 text-gray-300">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
              {/* Validation summary for step 3 */}
              {Object.keys(fieldErrors).length > 0 && (
                <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 space-y-1 w-full">
                  <p className="text-red-400 text-sm font-semibold flex items-center gap-2">&#9888; Please fix the following:</p>
                  {Object.values(fieldErrors).map((msg, i) => (
                    <p key={i} className="text-red-300 text-xs flex items-center gap-1">&#8226; {msg}</p>
                  ))}
                </div>
              )}
              <Button 
                onClick={() => handleStepNext(4, validateStep3)}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-black rounded-xl h-12"
              >
                Continue to Payment
              </Button>
            </div>

          </div>
        );
      
      // Step 4: Compliance & Payment
      case 4:
        return (
          <div className="space-y-5">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <ShieldCheck className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-white">Safety & Compliance</h2>
              <p className="text-gray-400 text-sm">Please confirm your package contents</p>
            </div>
            
            {/* Prohibited Items Questions - MUST ANSWER */}
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
              <h3 className="text-red-400 font-bold flex items-center gap-2 mb-2">
                <AlertTriangle className="h-5 w-5" />
                Safety Questions - Please Answer Honestly
              </h3>
              <p className="text-gray-400 text-xs mb-4">Leave all boxes unchecked if your package does NOT contain any prohibited items.</p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="cash" 
                    checked={containsCash} 
                    onCheckedChange={setContainsCash}
                    className="mt-1 border-red-400 data-[state=checked]:bg-red-500"
                  />
                  <label htmlFor="cash" className="text-gray-300 text-sm cursor-pointer">
                    Does this package contain <span className="text-red-400 font-bold">cash, checks, gift cards, or monetary instruments</span>?
                  </label>
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="weapons" 
                    checked={containsWeapons} 
                    onCheckedChange={setContainsWeapons}
                    className="mt-1 border-red-400 data-[state=checked]:bg-red-500"
                  />
                  <label htmlFor="weapons" className="text-gray-300 text-sm cursor-pointer">
                    Does this package contain <span className="text-red-400 font-bold">weapons, ammunition, explosives, or fireworks</span>?
                  </label>
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="drugs" 
                    checked={containsDrugs} 
                    onCheckedChange={setContainsDrugs}
                    className="mt-1 border-red-400 data-[state=checked]:bg-red-500"
                  />
                  <label htmlFor="drugs" className="text-gray-300 text-sm cursor-pointer">
                    Does this package contain <span className="text-red-400 font-bold">illegal drugs or controlled substances</span>?
                  </label>
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="hazmat" 
                    checked={containsHazmat} 
                    onCheckedChange={setContainsHazmat}
                    className="mt-1 border-red-400 data-[state=checked]:bg-red-500"
                  />
                  <label htmlFor="hazmat" className="text-gray-300 text-sm cursor-pointer">
                    Does this package contain <span className="text-red-400 font-bold">hazardous materials (flammables, chemicals, batteries)</span>?
                  </label>
                </div>
              </div>
            </div>
            
            {/* Block Message if prohibited item selected */}
            {complianceBlocked && (
              <div className="p-4 bg-red-600/20 border border-red-500 rounded-xl flex items-start gap-3">
                <XCircle className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-red-400">Cannot Ship This Package</p>
                  <p className="text-red-300 text-sm">{blockReason}</p>
                </div>
              </div>
            )}
            
            {/* Final Acknowledgment */}
            {!complianceBlocked && (
              <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="acknowledge" 
                    checked={complianceAcknowledged} 
                    onCheckedChange={setComplianceAcknowledged}
                    className="mt-1 border-emerald-500 data-[state=checked]:bg-emerald-500"
                  />
                  <label htmlFor="acknowledge" className="text-gray-300 text-sm cursor-pointer">
                    <span className="font-bold text-white">I confirm this package does not contain prohibited or illegal items and is safe for transport.</span>
                    <span className="block text-gray-500 text-xs mt-1">Violations may result in legal action and account termination.</span>
                  </label>
                </div>
              </div>
            )}
            
            {/* Order Summary */}
            <div className="p-4 bg-gray-800 rounded-xl space-y-3">
              <h3 className="text-white font-bold">Order Summary</h3>
              <div className="text-sm space-y-2">
                <div className="flex justify-between text-gray-400">
                  <span>Pickup {pickupMode === "PUDO" && <span className="text-amber-400 text-xs">(PUDO)</span>}</span>
                  <span className="text-right text-white">{pickupMode === "PUDO" && pickupPudoId ? pudos.find(p => p.id === pickupPudoId)?.name : pickupStreet}, {pickupParish}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Dropoff {dropoffMode === "PUDO" && <span className="text-cyan-400 text-xs">(PUDO)</span>}</span>
                  <span className="text-right text-white">{dropoffMode === "PUDO" && dropoffPudoId ? pudos.find(p => p.id === dropoffPudoId)?.name : dropoffStreet}, {dropoffParish}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Recipient</span>
                  <span className="text-white">{recipientName}</span>
                </div>
                {/* Multi-Package Display */}
                {packages.length > 0 ? (
                  <div className="space-y-1">
                    <div className="flex justify-between text-emerald-400 font-medium">
                      <span>Packages ({packages.length})</span>
                      <span className="text-white">{getTotalWeight()} lbs total</span>
                    </div>
                    {packages.map((pkg, idx) => (
                      <div key={pkg.id} className="flex justify-between text-gray-500 text-xs pl-4">
                        <span>#{idx + 1} {pkg.sizeLabel}</span>
                        <span>{pkg.weight} lbs</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex justify-between text-gray-400">
                    <span>Package</span>
                    <span className="text-white">{packageSize} - {packageWeight}lbs</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-400">
                  <span>Shipping</span>
                  <span className="text-white">${quote?.total_price?.toLocaleString() || '0'} JMD</span>
                </div>
                {isUrgent && urgentOfferAmount && (
                  <div className="flex justify-between text-red-400 font-medium">
                    <span className="flex items-center gap-1"><Truck className="h-3 w-3" /> Urgent Driver Bonus</span>
                    <span>${parseFloat(urgentOfferAmount).toLocaleString()} JMD</span>
                  </div>
                )}
                <div className="flex justify-between text-white font-bold text-lg pt-2 border-t border-gray-700">
                  <span>Total</span>
                  <span className="text-emerald-400">
                    ${((quote?.total_price || 0) + (isUrgent ? (parseFloat(urgentOfferAmount) || 0) : 0)).toLocaleString()} JMD
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => { setStep(3); setFieldErrors({}); }} className="flex-1 h-12 rounded-xl border-gray-700 text-gray-300">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
              <Button 
                onClick={handleCheckout}
                disabled={complianceBlocked || !complianceAcknowledged || isLoading}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl h-12 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
                ) : (
                  <><CreditCard className="h-4 w-4 mr-2" /> Proceed to Payment</>
                )}
              </Button>
            </div>
          </div>
        );
      
      // Step 5: Success (after Stripe redirect)
      case 5:
      case 6:
        return (
          <div className="text-center space-y-6">
            <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="h-10 w-10 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Package Booked!</h2>
              <p className="text-gray-400 mt-2">Your package has been successfully booked</p>
            </div>
            
            {createdPackage && (
              <div className="p-4 bg-gray-800 rounded-xl text-left">
                <p className="text-gray-400 text-sm">Tracking ID</p>
                <p className="text-emerald-400 font-mono text-lg">{createdPackage.tracking_id || createdPackage.id}</p>
              </div>
            )}
            
            <p className="text-gray-400 text-sm">
              📧 Confirmation email sent to {user?.email}
            </p>
            
            {/* Next-step options after successful payment */}
            <div className="grid grid-cols-1 gap-3">
              <Button
                data-testid="post-pay-book-another"
                onClick={() => { setStep(1); setCreatedPackage(null); setPaymentSuccess(false); }}
                className="h-12 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                <Package className="h-4 w-4 mr-2" /> Book another package
              </Button>
              <Button
                data-testid="post-pay-print-label"
                variant="outline"
                onClick={() => setShowLabel(true)}
                className="h-12 rounded-xl border-emerald-400/40 text-emerald-300"
              >
                <Printer className="h-4 w-4 mr-2" /> Print / view shipping label
              </Button>
              <Button
                data-testid="post-pay-home"
                variant="outline"
                onClick={() => navigate('/')}
                className="h-12 rounded-xl border-gray-700 text-gray-300"
              >
                <Home className="h-4 w-4 mr-2" /> Return to home
              </Button>
              <Button
                data-testid="post-pay-book-ride"
                variant="outline"
                onClick={() => navigate('/ride')}
                className="h-12 rounded-xl border-emerald-400/40 text-emerald-300"
              >
                Book a ride
              </Button>
              <Button
                data-testid="post-pay-shop-online"
                variant="outline"
                onClick={() => window.open('https://liantiaoja.com', '_blank', 'noopener')}
                className="h-12 rounded-xl border-gray-700 text-gray-300"
              >
                Shop online at liantiaoja.com
              </Button>
              {user?.must_set_password && (
                <Button
                  data-testid="post-pay-set-password"
                  variant="outline"
                  onClick={() => setShowSetPwd(true)}
                  className="h-12 rounded-xl border-emerald-400/40 text-emerald-300"
                >
                  <KeyRound className="h-4 w-4 mr-2" /> Set a password for faster checkout
                </Button>
              )}
              <button
                type="button"
                data-testid="post-pay-view-packages"
                onClick={() => navigate('/my-packages')}
                className="text-xs text-gray-400 hover:text-emerald-300 underline text-center mt-1"
              >
                Track my packages
              </button>
            </div>
          </div>
        );
      
      // Step 7: OPERATOR MODE SUCCESS - Payment Link Sharing
      case 7:
        if (!operatorMode || !assistedBookingResult) return null;
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-10 w-10 text-black" />
              </div>
              <h2 className="text-2xl font-bold text-white">Booking Created!</h2>
              <p className="text-gray-400 mt-2">Share the payment link with the customer</p>
            </div>
            
            <div className="p-4 bg-gray-800 rounded-xl space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Package ID</span>
                <span className="font-mono text-white">{assistedBookingResult.package_id}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Tracking ID</span>
                <span className="font-mono text-amber-400">{assistedBookingResult.tracking_id}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Total</span>
                <span className="font-bold text-emerald-400">J${assistedBookingResult.total_price?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Status</span>
                <span className="flex items-center gap-1.5 text-amber-400">
                  <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                  Awaiting Payment
                </span>
              </div>
            </div>
            
            <div className="space-y-3">
              <Label className="text-sm text-gray-300">Payment Link</Label>
              <div className="p-3 bg-gray-900 rounded-lg">
                <p className="text-xs text-emerald-400 font-mono break-all">{assistedBookingResult.payment_link}</p>
              </div>
              
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={async () => {
                    navigator.clipboard.writeText(paymentLinkOptions?.copy_text || assistedBookingResult.payment_link);
                    try { await api.post(`/assisted-booking/copy-link/${assistedBookingResult.package_id}`); } catch {}
                    toast.success("Copied to clipboard!");
                  }}
                  className="flex flex-col items-center gap-1 p-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                  data-testid="copy-link-btn"
                >
                  <Copy className="h-5 w-5 text-white" />
                  <span className="text-xs text-gray-300">Copy</span>
                </button>
                <button
                  onClick={async () => {
                    try { await api.post(`/assisted-booking/whatsapp-link/${assistedBookingResult.package_id}`); } catch {}
                    window.open(paymentLinkOptions?.whatsapp_link, "_blank");
                  }}
                  className="flex flex-col items-center gap-1 p-3 bg-green-600 hover:bg-green-500 rounded-lg transition-colors"
                  data-testid="whatsapp-btn"
                >
                  <MessageCircle className="h-5 w-5 text-white" />
                  <span className="text-xs text-white">WhatsApp</span>
                </button>
                <button
                  onClick={async () => {
                    try { await api.post(`/assisted-booking/resend-email/${assistedBookingResult.package_id}`); } catch {}
                    window.location.href = paymentLinkOptions?.email_link;
                  }}
                  className="flex flex-col items-center gap-1 p-3 bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors"
                  data-testid="email-btn"
                >
                  <Mail className="h-5 w-5 text-white" />
                  <span className="text-xs text-white">Email</span>
                </button>
              </div>
            </div>
            
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-xs text-amber-400 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                The package will only become visible to drivers after the customer completes payment. There is no manual "mark as paid" option.
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => navigate('/admin/assisted-booking-list')} 
                className="flex-1 h-12 rounded-xl border-gray-700 text-gray-300"
              >
                View All Bookings
              </Button>
              <Button 
                onClick={() => {
                  // Reset all state for new booking
                  setStep(0);
                  setAssistedBookingResult(null);
                  setPaymentLinkOptions(null);
                  setCustomerName("");
                  setCustomerPhone("");
                  setCustomerEmail("");
                  setOperatorNotes("");
                  setRecipientName("");
                  setRecipientPhone("");
                  setRecipientEmail("");
                  setPickupStreet("");
                  setPickupCity("");
                  setPickupParish("Kingston");
                  setPickupLandmark("");
                  setPickupCoords(null);
                  setDropoffStreet("");
                  setDropoffCity("");
                  setDropoffParish("Kingston");
                  setDropoffLandmark("");
                  setDropoffCoords(null);
                  setPackageSize("small");
                  setPackageWeight("");
                  setPackageContents("");
                  setContentsCategory("OTHER_NONPROHIBITED");
                  setIsFragile(false);
                  setIsUrgent(false);
                  setUrgentOfferAmount("");
                  setComplianceAcknowledged(false);
                  setFieldErrors({});
                }}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-black rounded-xl h-12 font-semibold"
                data-testid="new-booking-btn"
              >
                Create Another
              </Button>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };


  // R29 — guest checkout gate handlers
  const handleGuestStart = async () => {
    const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRx.test(gsEmail)) { toast.error('Enter a valid email'); return; }
    if (!gsPhone || gsPhone.replace(/\D/g,'').length < 7) { toast.error('Enter a valid phone number'); return; }
    if (!gsName || gsName.trim().length < 2) { toast.error('Enter your name'); return; }
    setGateBusy(true);
    try {
      await api.post('/guest-signup/start', { email: gsEmail.trim().toLowerCase(), phone: gsPhone.trim(), name: gsName.trim() });
      toast.success('Code sent — check your email');
      setGateStep('code');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send code. Please try again.');
    } finally { setGateBusy(false); }
  };

  const handleGuestVerify = async () => {
    if (!/^\d{6}$/.test(gsCode)) { toast.error('Enter the 6-digit code'); return; }
    setGateBusy(true);
    try {
      const res = await api.post('/guest-signup/verify', { email: gsEmail.trim().toLowerCase(), code: gsCode });
      setSession(res.data.access_token, res.data.user);
      setShowGuestGate(false);
      // Pre-fill sender fields with verified info
      setSenderEmail(res.data.user.email || gsEmail);
      toast.success('Email verified — continuing to payment');
      // R29 blocker-1 fix: flip a flag; a useEffect on [user, pendingAuthCheckout] will fire handleCheckout after user state lands
      setPendingAuthCheckout(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Verification failed');
    } finally { setGateBusy(false); }
  };

  // R29 blocker-1 fix: fire handleCheckout AFTER user is set + gate closed
  useEffect(() => {
    if (pendingAuthCheckout && user) {
      setPendingAuthCheckout(false);
      // Defer to next microtask so React finishes rendering
      Promise.resolve().then(() => { handleCheckout && handleCheckout(); });
    }
  }, [pendingAuthCheckout, user]);

  const handleSigninSubmit = async () => {
    const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRx.test(signinEmail)) { toast.error('Enter a valid email'); return; }
    if (!signinPassword) { toast.error('Enter your password'); return; }
    setGateBusy(true);
    try {
      const res = await api.post('/auth/login', { identifier: signinEmail.trim().toLowerCase(), password: signinPassword });
      setSession(res.data.access_token, res.data.user);
      setShowGuestGate(false);
      if (res.data.user.email) setSenderEmail(res.data.user.email);
      toast.success('Signed in — continuing to payment');
      setPendingAuthCheckout(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.response?.data?.error?.message || 'Sign in failed');
    } finally { setGateBusy(false); }
  };

  const handleLoginCodeStart = async () => {
    const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRx.test(signinEmail)) { toast.error('Enter your account email first'); return; }
    setLcBusy(true);
    try {
      await api.post('/auth/login-code/start', { email: signinEmail.trim().toLowerCase() });
      setLcSent(true);
      toast.success('If that account exists, a 6-digit code is on its way.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send code. Please try again.');
    } finally { setLcBusy(false); }
  };

  const handleLoginCodeVerify = async () => {
    if (!/^\d{6}$/.test(lcCode)) { toast.error('Enter the 6-digit code'); return; }
    setLcBusy(true);
    try {
      const res = await api.post('/auth/login-code/verify', { email: signinEmail.trim().toLowerCase(), code: lcCode });
      setSession(res.data.access_token, res.data.user);
      setShowGuestGate(false);
      if (res.data.user.email) setSenderEmail(res.data.user.email);
      toast.success('Signed in — continuing your booking.');
      setPendingAuthCheckout(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid or expired code.');
    } finally { setLcBusy(false); }
  };

  const handleSetPassword = async () => {
    if (newPwd.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (newPwd !== newPwd2) { toast.error('Passwords do not match'); return; }
    setPwdBusy(true);
    try {
      await api.post('/auth/set-password', { password: newPwd });
      if (user) setSession(token, { ...user, must_set_password: false });
      toast.success('Password saved — next time just sign in with email + password.');
      setShowSetPwd(false); setNewPwd(''); setNewPwd2('');
    } catch (err) {
      toast.error(err?.response?.data?.error?.message || err?.response?.data?.detail || 'Could not save password.');
    } finally { setPwdBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black py-8 px-4">
      <LaunchPromoBanner />
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" onClick={() => navigate(operatorMode ? '/admin' : -1)} className="text-gray-400 hover:text-white">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              {operatorMode && <Shield className="h-5 w-5 text-amber-500" />}
              {operatorMode ? 'Assisted Booking' : 'Send Package'}
            </h1>
            {operatorMode && <p className="text-xs text-gray-400">Booking on behalf of customer</p>}
          </div>
        </div>
        
        {/* Progress Steps */}
        {((operatorMode && step < 6) || (!operatorMode && step < 5)) && (
          <div className="flex items-center justify-center gap-2 mb-8">
            {(operatorMode ? [0, 1, 2, 3, 4, 5] : [1, 2, 3, 4]).map(s => (
              <div key={s} className={`h-2 rounded-full transition-all ${
                s === step ? `w-8 ${operatorMode ? 'bg-amber-500' : 'bg-emerald-500'}` : 
                s < step ? `w-8 ${operatorMode ? 'bg-amber-500/50' : 'bg-emerald-500/50'}` : 
                'w-8 bg-gray-700'
              }`} />
            ))}
          </div>
        )}
        
        {/* Main Content */}
        <div className="bg-gray-800/50 backdrop-blur rounded-2xl p-6 border border-gray-700">
          {renderStep()}
        </div>
      </div>

      {/* R29 — Guest Checkout Gate Modal */}
      {showGuestGate && (
        <div
          data-testid="guest-signup-gate"
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowGuestGate(false); }}
        >
          <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Almost there</h3>
              <button data-testid="guest-signup-close" onClick={() => setShowGuestGate(false)} className="text-slate-400 hover:text-white">✕</button>
            </div>
            <div className="flex gap-2 mb-4 text-sm">
              <button
                data-testid="guest-signup-tab-guest"
                onClick={() => { setGateTab('guest'); setGateStep('form'); }}
                className={'flex-1 py-2 rounded-lg ' + (gateTab === 'guest' ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-300')}
              >Continue as new user</button>
              <button
                data-testid="guest-signup-tab-signin"
                onClick={() => setGateTab('signin')}
                className={'flex-1 py-2 rounded-lg ' + (gateTab === 'signin' ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-300')}
              >Sign in</button>
            </div>

            {gateTab === 'signin' && (
              <div className="space-y-3">
                <label className="block text-xs text-slate-400">Email</label>
                <input data-testid="guest-signup-signin-email" type="email" value={signinEmail} onChange={(e) => setSigninEmail(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white" placeholder="you@example.com" />
                {!lcMode ? (
                  <>
                    <label className="block text-xs text-slate-400">Password</label>
                    <input data-testid="guest-signup-signin-password" type="password" value={signinPassword} onChange={(e) => setSigninPassword(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white" placeholder="Your password" />
                    <Button data-testid="guest-signup-signin-submit" onClick={handleSigninSubmit} disabled={gateBusy} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-11 rounded-lg">
                      {gateBusy ? 'Signing in...' : 'Sign in & continue'}
                    </Button>
                    <button
                      data-testid="guest-signup-forgot"
                      onClick={() => { setLcMode(true); setLcSent(false); setLcCode(''); }}
                      className="w-full text-xs text-emerald-300 hover:text-emerald-200 underline mt-1"
                    >
                      Forgot your password? Sign in with an email code instead
                    </button>
                  </>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs text-slate-400">We'll email a 6-digit sign-in code to <span className="text-emerald-300">{signinEmail || 'your email'}</span>. No password needed.</p>
                    {!lcSent ? (
                      <Button data-testid="login-code-send" onClick={handleLoginCodeStart} disabled={lcBusy} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-11 rounded-lg">
                        {lcBusy ? 'Sending...' : 'Send sign-in code'}
                      </Button>
                    ) : (
                      <>
                        <label className="block text-xs text-slate-400">Enter the 6-digit code</label>
                        <input data-testid="login-code-input" inputMode="numeric" maxLength={6} value={lcCode} onChange={(e) => setLcCode(e.target.value.replace(/\D/g, '').slice(0, 6))} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white tracking-[0.3em] text-center" placeholder="123456" />
                        <Button data-testid="login-code-verify" onClick={handleLoginCodeVerify} disabled={lcBusy} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-11 rounded-lg">
                          {lcBusy ? 'Verifying...' : 'Verify & continue'}
                        </Button>
                        <button data-testid="login-code-resend" onClick={handleLoginCodeStart} disabled={lcBusy} className="w-full text-xs text-emerald-300 hover:text-emerald-200 underline">Resend code</button>
                      </>
                    )}
                    <button data-testid="login-code-back" onClick={() => { setLcMode(false); setLcSent(false); setLcCode(''); }} className="w-full text-xs text-slate-400 hover:text-slate-200 underline">Back to password sign-in</button>
                  </div>
                )}
              </div>
            )}

            {gateTab === 'guest' && gateStep === 'form' && (
              <div className="space-y-3">
                <label className="block text-xs text-slate-400">Email</label>
                <input data-testid="guest-signup-email" type="email" value={gsEmail} onChange={(e) => setGsEmail(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white" placeholder="you@example.com" />
                <label className="block text-xs text-slate-400">Phone number</label>
                <input data-testid="guest-signup-phone" type="tel" value={gsPhone} onChange={(e) => setGsPhone(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white" placeholder="876 555 1234" />
                <label className="block text-xs text-slate-400">Full name</label>
                <input data-testid="guest-signup-name" type="text" value={gsName} onChange={(e) => setGsName(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white" placeholder="First Last" />
                <Button data-testid="guest-signup-send-code" onClick={handleGuestStart} disabled={gateBusy} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-11 rounded-lg">
                  {gateBusy ? 'Sending...' : 'Send verification code'}
                </Button>
                <p className="text-xs text-slate-500 text-center">We'll email a 6-digit code to verify it's you.</p>
              </div>
            )}

            {gateTab === 'guest' && gateStep === 'code' && (
              <div className="space-y-3">
                <p className="text-sm text-slate-300">Enter the 6-digit code we emailed to <span className="text-emerald-300">{gsEmail}</span></p>
                <input
                  data-testid="guest-signup-code"
                  inputMode="numeric" pattern="\d*" maxLength={6}
                  value={gsCode}
                  onChange={(e) => setGsCode(e.target.value.replace(/\D/g,'').slice(0,6))}
                  className="w-full text-center tracking-widest text-2xl font-bold px-3 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white"
                  placeholder="------"
                />
                <Button data-testid="guest-signup-verify-btn" onClick={handleGuestVerify} disabled={gateBusy || gsCode.length !== 6} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-11 rounded-lg">
                  {gateBusy ? 'Verifying...' : 'Verify & continue to payment'}
                </Button>
                <button data-testid="guest-signup-resend" onClick={() => setGateStep('form')} className="w-full text-xs text-emerald-300 hover:text-emerald-200 underline">
                  Change email / resend code
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Printable shipping label */}
      {showLabel && (
        <div
          data-testid="shipping-label-overlay"
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-auto no-print"
          onClick={(e) => { if (e.target === e.currentTarget) setShowLabel(false); }}
        >
          <div className="bg-white rounded-xl max-w-sm w-full shadow-2xl">
            <div id="shipping-label" className="p-6 text-black">
              <div className="flex items-center justify-between border-b-2 border-black pb-2 mb-3">
                <span className="font-extrabold text-lg tracking-tight">LIANTIAO GO</span>
                <span className="text-xs font-semibold uppercase">Shipping Label</span>
              </div>
              <div className="text-center mb-3">
                <p className="text-[10px] uppercase tracking-widest text-gray-500">Tracking ID</p>
                <p className="font-mono font-bold text-xl" data-testid="label-tracking">{createdPackage?.tracking_id || createdPackage?.id || '—'}</p>
                <div className="mt-1 font-mono text-[10px] leading-none tracking-[0.15em] break-all">||| |||| | || ||||| | |||  || ||||</div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="font-bold uppercase text-[10px] text-gray-500">From</p>
                  <p className="font-semibold">{user?.name || senderEmail || 'Sender'}</p>
                  <p>{pickupStreet}</p>
                  <p>{[pickupCity, pickupParish].filter(Boolean).join(', ')}</p>
                </div>
                <div>
                  <p className="font-bold uppercase text-[10px] text-gray-500">To</p>
                  <p className="font-semibold">{recipientName || 'Recipient'}</p>
                  <p>{recipientPhone}</p>
                  <p>{dropoffStreet}</p>
                  <p>{[dropoffCity, dropoffParish].filter(Boolean).join(', ')}</p>
                </div>
              </div>
              <div className="border-t border-gray-300 mt-3 pt-2 flex justify-between text-xs">
                <span>Size: <b>{packageSize || '—'}</b></span>
                <span>Weight: <b>{packageWeight ? packageWeight + ' lb' : '—'}</b></span>
              </div>
            </div>
            <div className="flex gap-2 p-4 border-t border-gray-200 no-print">
              <Button data-testid="label-print-btn" onClick={() => window.print()} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg">
                <Printer className="h-4 w-4 mr-2" /> Print
              </Button>
              <Button data-testid="label-close-btn" variant="outline" onClick={() => setShowLabel(false)} className="rounded-lg text-gray-700 border-gray-300">Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* Set a password (faster checkout next time) */}
      {showSetPwd && (
        <div
          data-testid="set-pwd-overlay"
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 no-print"
          onClick={(e) => { if (e.target === e.currentTarget) setShowSetPwd(false); }}
        >
          <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-white">Set a password</h3>
              <button data-testid="set-pwd-close" onClick={() => setShowSetPwd(false)} className="text-slate-400 hover:text-white">✕</button>
            </div>
            <p className="text-xs text-slate-400 mb-4">Add a password so next time you can sign in with just your email and password — no code needed.</p>
            <label className="block text-xs text-slate-400">New password</label>
            <input data-testid="set-pwd-input" type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white mb-2" placeholder="At least 8 characters" />
            <label className="block text-xs text-slate-400">Confirm password</label>
            <input data-testid="set-pwd-confirm" type="password" value={newPwd2} onChange={(e) => setNewPwd2(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white mb-4" placeholder="Re-enter password" />
            <div className="flex gap-2">
              <Button data-testid="set-pwd-save" onClick={handleSetPassword} disabled={pwdBusy} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg h-11">{pwdBusy ? 'Saving...' : 'Save password'}</Button>
              <Button data-testid="set-pwd-skip" variant="outline" onClick={() => setShowSetPwd(false)} className="rounded-lg text-gray-300 border-gray-700 h-11">Skip</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SendPackage;
